import { useState, useRef, useEffect, createContext, useContext } from "react";
import { certOfferFor, serviceLabel, serviceColor, findModelOption, CONFIGS, EQUIPMENT_TYPES, MODEL_OPTIONS, getModelGroup, getServiceActionsForModel, getSectionsForModel, ALL_MODEL_SECTION_EXTRA_IDS, resolveTemplate, getHirePhotosForModel, hirePhotoList, readingChange, rebuildResults, REBUILD_FLAG_PCT, targetRange, rangeStatus, targetText, REC_TOLERANCE_PCT } from "./checksheets";
import { exportPDF } from "./pdfExport";
import { readRebuildFromPdf } from "./quoteExport";
import LOGO_B64 from "./logoBase64";
import { makeColors, applyTheme, today } from "./theme";
import { getAllSheets, saveSheet, deleteSheet, getDarkMode, setDarkMode, onSaveStatus, lastSaveOk, getAutoSavePhotos, setAutoSavePhotos, flushSaves } from "./storage";
import { fixImageRotation } from "./photos";
import { BlueprintPanel } from "./blueprints";
import SignaturePad from "./SignaturePad";
import LEGACY_V39 from "./legacyLabels";

// Module-level color ref — updated by App when dark mode changes
let C = makeColors(getDarkMode());
// Apply theme immediately on load
applyTheme(getDarkMode());

// ─── Design system primitives ─────────────────────────────────────────────────
const v = (name) => `var(--${name})`;

const Label = ({ children }) => (
  <span style={{ fontSize:12, fontWeight:600, color:v("label"), textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:8 }}>{children}</span>
);

const FieldInput = ({ style, ...props }) => (
  <input style={{ fontSize:14, background:v("input"), color:v("text"), border:`1px solid ${v("border")}`, borderRadius:8, padding:"12px", width:"100%", minWidth:0, outline:"none", ...style }} {...props} />
);

const Btn = ({ children, style, variant="secondary", ...props }) => {
  const styles = {
    primary:   { background:"#2563EB", color:"#fff", border:"none" },
    secondary: { background:"transparent", color:v("text-sec"), border:`1px solid ${v("divider")}` },
    success:   { background:"#059669", color:"#fff", border:"none" },
    danger:    { background:"#DC2626", color:"#fff", border:"none" },
    ghost:     { background:"transparent", color:v("label"), border:"none" },
  };
  return (
    <button style={{ fontSize:14, fontWeight:500, padding:"12px 20px", borderRadius:8, cursor:"pointer", display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8, minHeight:44, transition:"all 150ms ease", ...styles[variant], ...style }} {...props}>
      {children}
    </button>
  );
};

const SectionWrap = ({ children, style }) => (
  <div style={{ background:v("surface"), border:`1px solid ${v("border")}`, borderRadius:12, marginBottom:16, overflow:"hidden", boxShadow:v("shadow"), ...style }}>{children}</div>
);

const SectionHead = ({ title, badges, collapsed, onToggle }) => (
  <div onClick={onToggle} style={{ background:v("surface2"), padding:"16px 20px", fontSize:14, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", userSelect:"none", color:v("text"), borderBottom: collapsed ? "none" : `1px solid ${v("border")}` }}>
    <span>{title}</span>
    <span style={{ display:"flex", alignItems:"center", gap:8 }}>
      {badges}
      <span style={{ fontSize:18, color:v("label"), display:"inline-block", transform:collapsed?"rotate(-90deg)":"rotate(0deg)", transition:"transform 0.2s" }}>⌄</span>
    </span>
  </div>
);

const ColHeader = () => (
  <div style={{ display:"grid", gridTemplateColumns:"1fr 64px 56px 64px", gap:8, padding:"10px 20px", background:v("surface2"), fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:v("label"), borderBottom:`1px solid ${v("border")}` }}>
    <span>Item</span>
    <span style={{ textAlign:"center" }}>Pass</span>
    <span style={{ textAlign:"center" }}>N/A</span>
    <span style={{ textAlign:"center" }}>Fail</span>
  </div>
);

const RadioBtn = ({ active, color, label, onClick }) => (
  <div style={{ display:"flex", alignItems:"center", justifyContent:"center" }}>
    <div onClick={onClick} style={{ width:48, height:48, borderRadius:"50%", border: active ? `2px solid ${color}` : `1px solid ${v("border")}`, background: active ? color : "transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color: active ? "#fff" : v("label"), userSelect:"none", transition:"all 150ms ease" }}>
      {label}
    </div>
  </div>
);

const SaBtn = ({ current, value, label, color, onClick }) => (
  <button onClick={onClick} style={{ fontSize:13, fontWeight:500, padding:"10px 16px", minHeight:44, border: current===value ? `1px solid ${color}` : `1px solid ${v("border")}`, borderRadius:8, cursor:"pointer", background: current===value ? color : "transparent", color: current===value ? "#fff" : v("text-sec"), transition:"all 150ms ease" }}>{label}</button>
);

const YnBtn = ({ active, label, color, onClick }) => (
  <button onClick={onClick} style={{ fontSize:13, fontWeight:500, padding:"10px 18px", minHeight:44, border: active ? `1px solid ${color}` : `1px solid ${v("border")}`, borderRadius:8, cursor:"pointer", background: active ? color : "transparent", color: active ? "#fff" : v("text-sec"), transition:"all 150ms ease" }}>{label}</button>
);

// Upload slot — spec: transparent bg, dashed border, no colour until upload
const UploadSlot = ({ onClick, label="Add photo", filled=false }) => (
  <button onClick={onClick} style={{ width:"100%", aspectRatio:"4/3", borderRadius:8, border: filled ? `1px solid #10B981` : `2px dashed ${v("divider")}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", background:"transparent", gap:8, color:v("label"), fontSize:13, fontWeight:500, transition:"border-color 150ms ease", minHeight:44 }}>
    <span style={{ fontSize:32 }}>📷</span>
    <span>{label}</span>
  </button>
);

// Photo picker popup
const PhotoPickerMenu = ({ show, onCamera, onFiles, onClose, style={} }) => show ? (
  <div style={{ position:"absolute", bottom:"calc(100% + 8px)", left:0, background:v("surface"), border:`1px solid ${v("border")}`, borderRadius:12, boxShadow:"0 8px 24px rgba(0,0,0,0.3)", zIndex:100, minWidth:200, overflow:"hidden", ...style }}>
    <button onClick={()=>{ onClose(); onCamera(); }} style={{ width:"100%", padding:"14px 18px", border:"none", background:"none", cursor:"pointer", fontSize:14, textAlign:"left", color:v("text"), display:"flex", alignItems:"center", gap:12, transition:"background 150ms ease" }}
      onMouseEnter={e=>e.currentTarget.style.background=v("surface2")} onMouseLeave={e=>e.currentTarget.style.background="none"}>
      📷 Take photo
    </button>
    <div style={{ height:1, background:v("border") }} />
    <button onClick={()=>{ onClose(); onFiles(); }} style={{ width:"100%", padding:"14px 18px", border:"none", background:"none", cursor:"pointer", fontSize:14, textAlign:"left", color:v("text"), display:"flex", alignItems:"center", gap:12, transition:"background 150ms ease" }}
      onMouseEnter={e=>e.currentTarget.style.background=v("surface2")} onMouseLeave={e=>e.currentTarget.style.background="none"}>
      🖼 Choose from files
    </button>
  </div>
) : null;

function initState(config, actions, sections) {
  const s = {
    _v:40, _pr:43, sectionNotes:{}, measures:{}, measurePhotos:{}, tlSignature:null,
    certs:{}, serviceActions:{}, oilSamplePhoto:null,
    vehiclePhotos:{ dashOn:null, front:null, driverSide:null, rear:null, passengerSide:null },
    hirePhotos:{},
    otherNotes:[ { text:"", photos:[], sendToZendesk:true } ],
    defects:[],
    signature: null,
  };
  (sections || config.sections).forEach(sec => { s[sec.id]={}; sec.items.forEach((_,i) => { s[sec.id][i]={status:null,note:"",photos:[]}; }); });
  config.certs?.forEach(c => { s.certs[c.id]={val:null,expiry:"",photos:[]}; });
  (actions || config.serviceActions)?.forEach(item => {
    s.serviceActions[item.key] = null;
    if (item.litres)    s.serviceActions[item.litres]    = "";
    if (item.condition) s.serviceActions[item.condition] = "";
  });
  if (config.operationalChecks) s.operationalChecks = { blowerVac:"", blowerPsi:"", wpUnloader:"" };
  return s;
}

// ─── Shared: photo thumbnail with delete + download ───────────────────────────
function PhotoThumb({ src, onRemove, onLightbox, downloadName, size=80, onMove }) {
  return (
    <div style={{ position:"relative", width:size, height:size*0.75, flexShrink:0 }}>
      <img src={src} alt="photo" onClick={()=>onLightbox(src)}
        style={{ width:"100%", height:"100%", borderRadius:8, objectFit:"cover", border:`1px solid ${v("border")}`, cursor:"pointer", display:"block" }} />
      <button onClick={()=>onRemove()}
        style={{ position:"absolute", top:-8, right:-8, background:"#DC2626", border:"none", borderRadius:"50%", width:22, height:22, cursor:"pointer", color:"#fff", fontSize:12, display:"flex", alignItems:"center", justifyContent:"center", padding:0, zIndex:2 }}>✕</button>
      <a href={src} download={downloadName||"photo.jpg"}
        style={{ position:"absolute", bottom:4, right:4, background:"rgba(0,0,0,0.6)", borderRadius:4, padding:"2px 6px", fontSize:11, color:"#fff", textDecoration:"none" }}
        onClick={e=>e.stopPropagation()}>⬇</a>
      {onMove && (
        <button onClick={e=>{ e.stopPropagation(); onMove(); }} title="Move or copy to another item"
          style={{ position:"absolute", bottom:4, left:4, background:"rgba(0,0,0,0.6)", border:"none", borderRadius:4, padding:"2px 7px", fontSize:12, color:"#fff", cursor:"pointer" }}>⇄</button>
      )}
    </div>
  );
}

// ─── Shared: inline photo picker with camera/files options ────────────────────
function InlinePhotoPicker({ onAdd, label="Add photo", color, compact=false }) {
  const cameraRef = useRef(null);
  const filesRef  = useRef(null);
  const [show, setShow] = useState(false);
  // Camera shots are also saved to the device (Android, if switched on); gallery picks are already there
  const handle = fromCamera => e => { Array.from(e.target.files).forEach(f=>fixImageRotation(f).then(src=>{ onAdd(src); if (fromCamera) savePhotoCopy(src); })); e.target.value=""; };
  const btnStyle = compact
    ? { width:80, height:60, borderRadius:8, border:`2px dashed ${color||v("divider")}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", background:"transparent", gap:4, color:color||v("label"), fontSize:11, fontWeight:500 }
    : { width:"100%", aspectRatio:"4/3", borderRadius:8, border:`2px dashed ${v("divider")}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"pointer", background:"transparent", gap:8, color:v("label"), fontSize:13, fontWeight:500, minHeight:44 };
  return (
    <div style={{ position:"relative", ...(compact ? { flexShrink:0 } : {}) }}>
      <button onClick={()=>setShow(p=>!p)} style={btnStyle}>
        <span style={{ fontSize: compact ? 20 : 28 }}>📷</span>
        <span>{label}</span>
      </button>
      <PhotoPickerMenu show={show} onClose={()=>setShow(false)} onCamera={()=>cameraRef.current?.click()} onFiles={()=>filesRef.current?.click()} />
      <input type="file" accept="image/*" multiple capture="environment" style={{ display:"none" }} ref={cameraRef} onChange={handle(true)} />
      <input type="file" accept="image/*" multiple style={{ display:"none" }} ref={filesRef} onChange={handle(false)} />
    </div>
  );
}

// ─── Shared: scroll-wheel date picker ─────────────────────────────────────────
// Every date field uses this instead of the phone's calendar: tap the field,
// then scroll Day, Month and Year wheels. Value is stored as "YYYY-MM-DD".
const WHEEL_ITEM = 40;
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function Wheel({ items, index, onChange, label }) {
  const ref = useRef(null);
  const timer = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = index * WHEEL_ITEM; }, [index]);
  const onScroll = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!ref.current) return;
      const i = Math.max(0, Math.min(items.length - 1, Math.round(ref.current.scrollTop / WHEEL_ITEM)));
      if (i !== index) onChange(i);
    }, 90);
  };
  return (
    <div ref={ref} onScroll={onScroll} aria-label={label} className="dw-wheel"
      style={{ flex:1, height:WHEEL_ITEM*5, overflowY:"auto", scrollSnapType:"y mandatory", WebkitOverflowScrolling:"touch", scrollbarWidth:"none", position:"relative", zIndex:1 }}>
      <div style={{ height:WHEEL_ITEM*2 }} />
      {items.map((it, i) => (
        <div key={i} onClick={() => ref.current.scrollTo({ top:i*WHEEL_ITEM, behavior:"smooth" })}
          style={{ height:WHEEL_ITEM, lineHeight:`${WHEEL_ITEM}px`, textAlign:"center", scrollSnapAlign:"center", fontSize:18, cursor:"pointer",
            fontWeight:i===index?700:400, color:i===index?v("text"):v("text-sec"), opacity:Math.abs(i-index)>1?0.45:1 }}>{it}</div>
      ))}
      <div style={{ height:WHEEL_ITEM*2 }} />
    </div>
  );
}
function parseIsoDate(val) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val || "");
  return m ? { y:+m[1], m:+m[2]-1, d:+m[3] } : null;
}
function DateWheelField({ value, onChange, placeholder = "Tap to pick date", allowClear = false, style }) {
  const [sel, setSel] = useState(null);   // picker open when set: { y, m, d }
  const now = new Date();
  const open = () => setSel(parseIsoDate(value) || { y:now.getFullYear(), m:now.getMonth(), d:now.getDate() });
  const pad = n => String(n).padStart(2, "0");
  const set = (s) => { const max = new Date(s.y, s.m + 1, 0).getDate(); setSel({ ...s, d:Math.min(s.d, max) }); };
  const years = [];
  if (sel) { const lo = Math.min(now.getFullYear() - 5, sel.y), hi = Math.max(now.getFullYear() + 15, sel.y); for (let y = lo; y <= hi; y++) years.push(y); }
  const days = sel ? Array.from({ length:new Date(sel.y, sel.m + 1, 0).getDate() }, (_, i) => i + 1) : [];
  const shown = parseIsoDate(value) ? fmtDate(value) : (value || "");
  return (
    <>
      <button type="button" onClick={open}
        style={{ textAlign:"left", fontSize:15, padding:"10px 12px", borderRadius:8, border:`1px solid ${v("border")}`, background:v("input"), color:shown?v("text"):v("text-ter"), cursor:"pointer", minHeight:44, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", ...style }}>
        {shown || placeholder}
      </button>
      {sel && (
        <div onClick={() => setSel(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:3000, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <style>{`.dw-wheel::-webkit-scrollbar{display:none}`}</style>
          <div onClick={e => e.stopPropagation()} style={{ width:"100%", maxWidth:440, background:v("surface"), borderRadius:"16px 16px 0 0", padding:"8px 16px calc(16px + env(safe-area-inset-bottom, 0px))", boxShadow:"0 -8px 30px rgba(0,0,0,0.25)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"6px 0 10px" }}>
              <button type="button" onClick={() => setSel(null)} style={{ background:"none", border:"none", fontSize:16, color:v("text-sec"), padding:8, cursor:"pointer" }}>Cancel</button>
              {allowClear && value
                ? <button type="button" onClick={() => { onChange(""); setSel(null); }} style={{ background:"none", border:"none", fontSize:14, color:"#DC2626", padding:8, cursor:"pointer" }}>Clear</button>
                : <span style={{ fontSize:15, fontWeight:600, color:v("text") }}>Pick date</span>}
              <button type="button" onClick={() => { onChange(`${sel.y}-${pad(sel.m + 1)}-${pad(sel.d)}`); setSel(null); }}
                style={{ background:"#1E5FA8", border:"none", borderRadius:8, fontSize:16, fontWeight:700, color:"#fff", padding:"8px 18px", cursor:"pointer" }}>Done</button>
            </div>
            <div style={{ display:"flex", fontSize:12, fontWeight:600, color:v("text-sec"), textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"center" }}>
              <span style={{ flex:1 }}>Day</span><span style={{ flex:1 }}>Month</span><span style={{ flex:1 }}>Year</span>
            </div>
            <div style={{ position:"relative", display:"flex", gap:8 }}>
              <div style={{ position:"absolute", left:0, right:0, top:WHEEL_ITEM*2, height:WHEEL_ITEM, borderRadius:8, background:"rgba(30,95,168,0.10)", border:"1px solid rgba(30,95,168,0.35)", pointerEvents:"none" }} />
              <Wheel label="Day"   items={days}        index={sel.d - 1}               onChange={i => set({ ...sel, d:i + 1 })} />
              <Wheel label="Month" items={MONTH_NAMES} index={sel.m}                   onChange={i => set({ ...sel, m:i })} />
              <Wheel label="Year"  items={years}       index={years.indexOf(sel.y)}    onChange={i => set({ ...sel, y:years[i] })} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Shared: lightbox ─────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <img src={src} alt="Full" style={{ maxWidth:"92vw", maxHeight:"88vh", borderRadius:12, objectFit:"contain" }} />
      <button onClick={onClose} style={{ position:"absolute", top:16, right:16, background:v("surface"), border:"none", borderRadius:"50%", width:44, height:44, cursor:"pointer", fontSize:20, color:v("text") }}>✕</button>
    </div>
  );
}

// ─── Photo locations (move / copy between entries) ────────────────────────────
// A photo's place on the sheet is described by a "loc":
//   { t:"item", sec, i }       first fault of a failed checklist item
//   { t:"fault", sec, i, id }  an extra fault on that item
//   { t:"cert", id }           failed cert
//   { t:"hire", key }          condition photo slot
//   { t:"measure", id }        measurement group photos
//   { t:"note", idx }          Other Notes entry
// Photos on failed items / certs are mirrored onto their defect, so every
// change goes through setPhotoListAt to keep the two in step.
function photoListAt(p, loc) {
  switch (loc.t) {
    case "item":    return p[loc.sec]?.[loc.i]?.photos || [];
    case "fault":   return (p[loc.sec]?.[loc.i]?.extraFaults || []).find(f => f.id === loc.id)?.photos || [];
    case "cert":    return p.certs?.[loc.id]?.photos || [];
    case "hire":    return hirePhotoList(p, loc.key);
    case "measure": return p.measurePhotos?.[loc.id] || [];
    case "note":    return p.otherNotes?.[loc.idx]?.photos || [];
    default:        return [];
  }
}
function setPhotoListAt(p, loc, list) {
  const sync = key => (p.defects || []).map(d => d.failKey === key ? { ...d, photos: list } : d);
  switch (loc.t) {
    case "item": {
      const it = p[loc.sec]?.[loc.i] || {};
      return { ...p, [loc.sec]: { ...p[loc.sec], [loc.i]: { ...it, photos: list } }, defects: sync(`${loc.sec}-${loc.i}`) };
    }
    case "fault": {
      const it = p[loc.sec]?.[loc.i] || {};
      const extraFaults = (it.extraFaults || []).map(f => f.id === loc.id ? { ...f, photos: list } : f);
      return { ...p, [loc.sec]: { ...p[loc.sec], [loc.i]: { ...it, extraFaults } }, defects: sync(`${loc.sec}-${loc.i}#${loc.id}`) };
    }
    case "cert":    return { ...p, certs: { ...p.certs, [loc.id]: { ...p.certs?.[loc.id], photos: list } }, defects: sync(`certs-${loc.id}`) };
    case "hire":    return { ...p, hirePhotos: { ...(p.hirePhotos || {}), [loc.key]: list } };
    case "measure": return { ...p, measurePhotos: { ...(p.measurePhotos || {}), [loc.id]: list } };
    case "note": {
      const notes = p.otherNotes.map((n, j) => j === loc.idx ? { ...n, photos: list } : n);
      const last = notes[notes.length - 1];   // keep a blank note at the end, as typing in one does
      if (last.text || last.photos?.length) notes.push({ text: "", photos: [], sendToZendesk: true });
      return { ...p, otherNotes: notes };
    }
    default: return p;
  }
}
const locKey = loc => JSON.stringify(loc);
// Every place a photo can be moved to on this sheet, in on-screen order
function photoTargets(state, eff) {
  const out = [];
  (eff.certs || []).forEach(c => { if (state.certs?.[c.id]?.val === false) out.push({ loc:{ t:"cert", id:c.id }, label:`Cert fail — ${c.label}` }); });
  (eff.hirePhotos || []).forEach(s => out.push({ loc:{ t:"hire", key:s.key }, label:`Condition photo — ${s.label}` }));
  eff.sections.forEach(sec => sec.items.forEach((label, i) => {
    const it = state[sec.id]?.[i];
    if (it?.status !== "fail") return;
    out.push({ loc:{ t:"item", sec:sec.id, i }, label:`Fail — ${label}${it.note ? `: ${it.note}` : ""}` });
    (it.extraFaults || []).forEach((f, n) => out.push({ loc:{ t:"fault", sec:sec.id, i, id:f.id }, label:`Fail — ${label} (fault ${n + 2})${f.note ? `: ${f.note}` : ""}` }));
  }));
  (eff.measurements || []).forEach(g => { if (g.photos) out.push({ loc:{ t:"measure", id:g.id }, label:`${g.title} — ${g.photos.label}` }); });
  (state.otherNotes || []).forEach((n, idx) => {
    const last = idx === state.otherNotes.length - 1;
    if (n.text || n.photos?.length) out.push({ loc:{ t:"note", idx }, label:`Other note — ${n.text ? n.text.slice(0, 40) : "(photos only)"}` });
    else if (last) out.push({ loc:{ t:"note", idx }, label:"Other notes — new note" });
  });
  return out;
}
// Provided by the sheet: { update(loc, fn), openMove(loc, src) }
const PhotoCtx = createContext(null);

// Android phones / tablets can drop each new photo into Downloads (shows in the
// Gallery) as it's taken. iPhones / iPads can't do this from a web app. Android
// tablets often run Chrome in "desktop site" mode and don't say "Android", so
// any touch device that isn't iOS gets the option.
const UA = typeof navigator !== "undefined" ? navigator.userAgent : "";
const TOUCH = typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
const IS_IOS = /iPad|iPhone|iPod/.test(UA) || (/Macintosh/.test(UA) && TOUCH);
const CAN_AUTOSAVE = !IS_IOS && (/android/i.test(UA) || TOUCH);
function savePhotoCopy(src) {
  if (!CAN_AUTOSAVE || !getAutoSavePhotos()) return;
  const name = `waimea-photo-${new Date().toISOString().replace(/[:.]/g, "-")}.jpg`;
  fetch(src).then(r => r.blob()).then(blob => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }).catch(() => {});
}

// ─── Fail photo row ───────────────────────────────────────────────────────────
// With `loc`, photos get a ⇄ button to move / copy them to another entry, and
// adds / removes go through the shared photo updater (safe for multi-select).
function FailPhotoRow({ photos, onAdd, onRemove, title="Fail photos", color="#EF4444", loc }) {
  const [lightbox, setLightbox] = useState(null);
  const ctx = useContext(PhotoCtx);
  const add    = loc && ctx ? src => ctx.update(loc, l => [...l, src]) : onAdd;
  const remove = loc && ctx ? pi  => ctx.update(loc, l => l.filter((_, j) => j !== pi)) : onRemove;
  return (
    <div style={{ padding:"12px 16px 14px", background:v("surface2"), borderTop:`1px solid ${v("border")}` }}>
      <div style={{ fontSize:11, fontWeight:700, color, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>{title}</div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"flex-start" }}>
        {photos.map((src,pi) => (
          <PhotoThumb key={pi} src={src} size={88} onLightbox={setLightbox} onRemove={()=>remove(pi)} downloadName={`fail-photo-${pi+1}.jpg`}
            onMove={loc && ctx ? () => ctx.openMove(loc, src) : undefined} />
        ))}
        <InlinePhotoPicker onAdd={add} label="Add" compact={true} color={color} />
      </div>
      <Lightbox src={lightbox} onClose={()=>setLightbox(null)} />
    </div>
  );
}

// Bottom sheet listing every other place the photo can go, with Move / Copy
function MovePhotoSheet({ moving, targets, onMove, onClose }) {
  if (!moving) return null;
  const options = targets.filter(t => locKey(t.loc) !== locKey(moving.loc));
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:3000, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:520, maxHeight:"80vh", display:"flex", flexDirection:"column", background:v("surface"), borderRadius:"16px 16px 0 0", boxShadow:"0 -8px 30px rgba(0,0,0,0.25)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderBottom:`1px solid ${v("border")}` }}>
          <img src={moving.src} alt="" style={{ width:64, height:48, objectFit:"cover", borderRadius:6 }} />
          <div style={{ flex:1, fontSize:15, fontWeight:600, color:v("text") }}>Move or copy this photo to…</div>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:16, color:v("text-sec"), padding:8, cursor:"pointer" }}>Cancel</button>
        </div>
        <div style={{ overflowY:"auto", padding:"4px 0 calc(12px + env(safe-area-inset-bottom, 0px))" }}>
          {options.length === 0 && <div style={{ padding:20, fontSize:14, color:v("text-sec") }}>Nowhere else to put it yet — fail an item or add a note first.</div>}
          {options.map(t => (
            <div key={locKey(t.loc)} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 16px", borderBottom:`1px solid ${v("border")}` }}>
              <span style={{ flex:1, fontSize:14, color:v("text"), lineHeight:1.35 }}>{t.label}</span>
              <button onClick={()=>onMove(t.loc, false)} style={{ fontSize:13, fontWeight:600, padding:"8px 14px", borderRadius:8, border:"none", background:"#1E5FA8", color:"#fff", cursor:"pointer" }}>Move</button>
              <button onClick={()=>onMove(t.loc, true)} style={{ fontSize:13, fontWeight:600, padding:"8px 14px", borderRadius:8, border:`1px solid ${v("border")}`, background:"transparent", color:v("text-sec"), cursor:"pointer" }}>Copy</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── Vehicle photos section ───────────────────────────────────────────────────
const VEHICLE_PHOTO_SLOTS = [
  { key:"dashOn",        label:"Dash — ignition on" },
  { key:"front",         label:"Vehicle front" },
  { key:"driverSide",    label:"Driver side" },
  { key:"rear",          label:"Rear of vehicle" },
  { key:"passengerSide", label:"Passenger side" },
];

function VehiclePhotosSection({ photos, onUpdate, collapsed, onToggle }) {
  const [lightbox, setLightbox] = useState(null);
  const taken = VEHICLE_PHOTO_SLOTS.filter(s=>photos[s.key]).length;
  const badge = taken === 5
    ? { background:"var(--success-bg)", color:"var(--success-text)", border:"1px solid var(--success-border)" }
    : { background:v("surface"), color:v("label"), border:`1px solid ${v("border")}` };
  return (
    <SectionWrap>
      <SectionHead title="Vehicle Photos" collapsed={collapsed} onToggle={onToggle} badges={
        <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, ...badge }}>{taken}/5</span>
      } />
      {!collapsed && (
        <div style={{ padding:"20px" }}>
          <p style={{ fontSize:13, color:v("label"), margin:"0 0 16px" }}>Reference photos — not shown in defects.</p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            {VEHICLE_PHOTO_SLOTS.map(slot => (
              <div key={slot.key} data-req={`p:${slot.key}`}>
                <Label>{slot.label}</Label>
                {photos[slot.key] ? (
                  <div style={{ position:"relative" }}>
                    <img src={photos[slot.key]} alt={slot.label} onClick={()=>setLightbox(photos[slot.key])}
                      style={{ width:"100%", aspectRatio:"4/3", borderRadius:8, objectFit:"cover", border:`1px solid #10B981`, cursor:"pointer", display:"block" }} />
                    <button onClick={()=>onUpdate(slot.key, null)}
                      style={{ position:"absolute", top:-8, right:-8, background:"#DC2626", border:"none", borderRadius:"50%", width:24, height:24, cursor:"pointer", color:"#fff", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", padding:0 }}>✕</button>
                  </div>
                ) : (
                  <InlinePhotoPicker onAdd={src=>onUpdate(slot.key,src)} label="Add photo" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <Lightbox src={lightbox} onClose={()=>setLightbox(null)} />
    </SectionWrap>
  );
}


// ─── Condition photos (off-hire / pre-hire) ───────────────────────────────────
// Record of how the unit is leaving us. Each slot needs at least one photo.
function HirePhotosSection({ slots, state, onUpdate, collapsed, onToggle }) {
  const done = slots.filter(s=>hirePhotoList(state, s.key).length>0).length;
  const badge = done === slots.length
    ? { background:"var(--success-bg)", color:"var(--success-text)", border:"1px solid var(--success-border)" }
    : { background:v("surface"), color:v("label"), border:`1px solid ${v("border")}` };
  return (
    <SectionWrap>
      <SectionHead title="Condition Photos" collapsed={collapsed} onToggle={onToggle} badges={
        <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, ...badge }}>{done}/{slots.length}</span>
      } />
      {!collapsed && (
        <div>
          <p style={{ fontSize:13, color:v("label"), margin:0, padding:"14px 16px 10px" }}>Record of how the unit is leaving us, so there's no dispute about what was dirty when it comes back. Add as many photos as needed.</p>
          {slots.map(slot => {
            const list = hirePhotoList(state, slot.key);
            return (
              <div key={slot.key} data-req={`hp:${slot.key}`}>
                <FailPhotoRow title={`${slot.label} *`} color="#1E5FA8" photos={list} loc={{ t:"hire", key:slot.key }}
                  onAdd={src=>onUpdate(slot.key, l=>[...l, src])}
                  onRemove={pi=>onUpdate(slot.key, l=>l.filter((_,j)=>j!==pi))} />
              </div>
            );
          })}
        </div>
      )}
    </SectionWrap>
  );
}


// ─── Inspection section ───────────────────────────────────────────────────────
function InspectionSection({ sec, secState, onUpdate, onFail, onFaults, collapsed, onToggle, failStartNum, sectionNote, onSectionNote, before, itemPhotos }) {
  const [photoOpen, setPhotoOpen] = useState({});   // item index -> optional photo row open
  const items = sec.items;
  const done  = items.filter((_,i) => secState?.[i]?.status).length;
  const fails = items.filter((_,i) => secState?.[i]?.status==="fail").length;
  let localFailCount = 0;

  return (
    <SectionWrap>
      <SectionHead title={sec.title} collapsed={collapsed} onToggle={onToggle} badges={<>
        {fails>0 && <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, background:"var(--danger-bg)", color:"var(--danger-text)" }}>{fails} fail</span>}
        <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, background:done===items.length?"#E3F5EC":"#FEF3E2", color:done===items.length?"#145C33":"#7A4A08" }}>{done}/{items.length}</span>
      </>} />
      {!collapsed && <div>
        <ColHeader />
        {items.map((label,i) => {
          const st = secState?.[i]||{status:null,note:"",photos:[]};
          if (st.status==="fail") localFailCount++;
          const failNum = st.status==="fail" ? failStartNum + localFailCount : null;
          return (
            <div key={i} data-req={`i:${sec.id}:${i}`}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 60px 55px 60px", gap:6, alignItems:"center", padding:"10px 12px", borderTop:`1px solid ${v("border")}`, background:st.status==="fail"?"var(--danger-bg)":i%2===0?"var(--surface)":"var(--surface2)" }}>
                <span style={{ fontSize:14, color:st.status==="fail"?"#8B1A14":v("text"), lineHeight:1.4 }}>
                  {label}
                  {failNum && <span style={{ marginLeft:8, fontSize:11, fontWeight:700, background:"#EF4444", color:"#fff", borderRadius:100, padding:"1px 7px" }}>#{failNum}</span>}
                </span>
                <RadioBtn active={st.status==="pass"} color={"#10B981"} label="Pass" onClick={()=>{
                  if(st.status==="fail") onFail(sec.id, i, label, false, st.note, st.photos);
                  onUpdate(i,"status",st.status==="pass"?null:"pass");
                }} />
                <RadioBtn active={st.status==="na"} color={"#94A3B8"} label="N/A" onClick={()=>{
                  if(st.status==="fail") onFail(sec.id, i, label, false, st.note, st.photos);
                  onUpdate(i,"status",st.status==="na"?null:"na");
                }} />
                <RadioBtn active={st.status==="fail"} color={"#EF4444"} label="Fail" onClick={()=>{
                  const nowFail = st.status!=="fail";
                  onUpdate(i,"status",nowFail?"fail":null);
                  onFail(sec.id, i, label, nowFail, st.note, st.photos);
                }} />
              </div>
              {before && (() => { const b = before.find(x => x.label === label) || before[i];
                return b ? <div style={{ background:st.status==="fail"?"var(--danger-bg)":i%2===0?"var(--surface)":"var(--surface2)", padding:"0 0 1px" }}><BeforeBox result={b.status || null}>{b.note && <span>— {b.note}</span>}</BeforeBox></div> : null; })()}
              <div style={{ padding:"0 12px 6px 12px", background:st.status==="fail"?"var(--danger-bg)":i%2===0?"var(--surface)":"var(--surface2)", display:"flex", gap:8, alignItems:"center" }}>
                <FieldInput style={{ fontSize:13, padding:"7px 10px" }} placeholder={st.status==="fail" ? "What's wrong?" : "Note…"} defaultValue={st.note}
                  onBlur={e=>{
                    onUpdate(i,"note",e.target.value);
                    if(st.status==="fail") onFail(sec.id, i, label, true, e.target.value, st.photos);
                  }} />
                {itemPhotos && st.status!=="fail" && (
                  <button onClick={()=>setPhotoOpen(o=>({...o,[i]:!o[i]}))} title="Add a photo to this item"
                    style={{ flexShrink:0, background:"transparent", border:`1px solid ${v("border")}`, borderRadius:8, padding:"6px 10px", fontSize:14, cursor:"pointer", color:v("text-sec") }}>
                    📷{(st.photos||[]).length ? <span style={{ fontSize:11, fontWeight:700, marginLeft:4 }}>{st.photos.length}</span> : null}
                  </button>
                )}
              </div>
              {/* Optional photos on a passed / N/A item (e.g. showing a fix) — fail photos sit below as usual */}
              {itemPhotos && st.status!=="fail" && (photoOpen[i] || (st.photos||[]).length>0) && (
                <FailPhotoRow title="Photos" color="#1E5FA8" loc={{ t:"item", sec:sec.id, i }} photos={st.photos||[]} />
              )}
              {st.status==="fail" && (
                <FailPhotoRow title="Fail photos" loc={{ t:"item", sec:sec.id, i }} photos={st.photos||[]} />
              )}
              {/* Extra faults on the same item — each becomes its own defect line */}
              {st.status==="fail" && (st.extraFaults||[]).map((f, n) => (
                <div key={f.id} style={{ borderTop:"1px dashed #EF4444", background:"var(--danger-bg)" }}>
                  <div style={{ display:"flex", gap:8, alignItems:"center", padding:"8px 12px 6px" }}>
                    <span style={{ fontSize:12, fontWeight:700, color:"#8B1A14", whiteSpace:"nowrap" }}>Fault {n+2}</span>
                    <FieldInput style={{ fontSize:13, padding:"7px 10px" }} placeholder="What's wrong?" defaultValue={f.note}
                      onBlur={e=>onFaults(sec.id, i, label, (st.extraFaults||[]).map(x=>x.id===f.id?{...x, note:e.target.value}:x))} />
                    <button onClick={()=>{ if(window.confirm(`Remove fault ${n+2}?`)) onFaults(sec.id, i, label, (st.extraFaults||[]).filter(x=>x.id!==f.id)); }}
                      style={{ flexShrink:0, background:"transparent", border:`1px solid ${v("border")}`, borderRadius:8, padding:"6px 10px", color:v("text-sec"), cursor:"pointer" }}>✕</button>
                  </div>
                  <FailPhotoRow title={`Fault ${n+2} photos`} loc={{ t:"fault", sec:sec.id, i, id:f.id }} photos={f.photos||[]} />
                </div>
              ))}
              {st.status==="fail" && (
                <div style={{ padding:"8px 12px 10px", background:"var(--danger-bg)" }}>
                  <button onClick={()=>onFaults(sec.id, i, label, [...(st.extraFaults||[]), { id:`f${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`, note:"", photos:[] }])}
                    style={{ fontSize:13, fontWeight:600, padding:"8px 14px", borderRadius:8, border:"1px dashed #EF4444", background:"transparent", color:"#B42318", cursor:"pointer" }}>+ Add another fault</button>
                </div>
              )}
            </div>
          );
        })}
        <div style={{ padding:"8px 12px 12px", borderTop:`1px solid ${v("border")}`, background:v("surface2"), display:"flex", gap:8, alignItems:"center" }}>
          <span style={{ fontSize:16, color:v("text-ter") }}>✎</span>
          <FieldInput style={{ fontSize:13 }} placeholder="Section notes…" defaultValue={sectionNote||""} onBlur={e=>onSectionNote(e.target.value)} />
        </div>
      </div>}
    </SectionWrap>
  );
}

// Cert result from its expiry date: past = fail, within a month = "due", else pass.
// (Pre-rebuild certs that are offered on the quote use a 3-month window.)
function certStatusFromExpiry(iso, months = 1) {
  const d = parseIsoDate(iso);
  if (!d) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiry = new Date(d.y, d.m, d.d);
  if (expiry < today) return false;
  const dueBy = new Date(today.getFullYear(), today.getMonth() + months, today.getDate());
  return expiry < dueBy ? "due" : true;
}
const isCertAnswered = val => val === true || val === false || val === "due";

function CertsSection({ config, certs, secState, onUpdate, onFail, collapsed, onToggle, before }) {
  const done = certs.filter(c => { const cur = secState?.[c.id]||{}; return isCertAnswered(cur.val) && (!c.hasExpiry || cur.expiry); }).length;
  const fails = certs.filter(c => secState?.[c.id]?.val===false).length;
  const dues = certs.filter(c => secState?.[c.id]?.val==="due").length;
  const failLabel = (c, expiry) => `${c.label}${expiry ? ` (expiry ${fmtDate(expiry)})` : ""}`;
  // Pre-rebuild: a cert expiring within the rebuild window is Due and goes on
  // the Defects list (so it reaches the quote) with a renewal recommendation.
  const offer = c => certOfferFor(config, c.id);
  const isDefect = (c, val) => val === false || (val === "due" && !!offer(c));
  const defectLabel = (c, val, expiry) => val === "due"
    ? `${c.label}${expiry ? ` expires ${fmtDate(expiry)}` : ""}: due within ${offer(c).months} months, renewal to offer`
    : failLabel(c, expiry);
  const offerRec = c => `Offer to renew the ${c.label.replace(/ — /, " ").replace(/\s*cert$/i, "")} cert during the rebuild, before it expires.`;
  // Set Pass / Due / Fail (or clear with null), keeping the defect list in step
  const setResult = (c, cur, val, expiry = cur.expiry) => {
    const was = isDefect(c, cur.val), now = isDefect(c, val);
    if (was || now) onFail("certs", c.id, defectLabel(c, val, expiry), now, val === false ? (cur.note||"") : "", val === false ? (cur.photos||[]) : [],
      val === "due" ? { recommendation: offerRec(c), sendToZendesk: false }   // a renewal to offer, not a fault
        : cur.val === "due" ? { sendToZendesk: true } : null);                // now a real fail: back to Zendesk
    onUpdate(c.id, "val", val);
  };
  return (
    <SectionWrap>
      <SectionHead title="Certs / Compliance" collapsed={collapsed} onToggle={onToggle} badges={<>
        {fails>0 && <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, background:"var(--danger-bg)", color:"var(--danger-text)" }}>{fails} fail</span>}
        {dues>0 && <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, background:"#FEF3E2", color:"#7A4A08" }}>{dues} due</span>}
        <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, background:done===certs.length?"#E3F5EC":"#FEF3E2", color:done===certs.length?"#145C33":"#7A4A08" }}>{done}/{certs.length}</span>
      </>} />
      {!collapsed && certs.map(c => {
        const cur = secState?.[c.id]||{val:null,expiry:"",note:""};
        const isFail = cur.val===false, isDue = cur.val==="due";
        // Old sheets stored the expiry as free text — only feed real dates to the date picker.
        const iso = /^\d{4}-\d{2}-\d{2}$/.test(cur.expiry||"") ? cur.expiry : "";
        return (
          <div key={c.id} data-req={`c:${c.id}`} style={{ borderTop:`1px solid ${v("border")}`, padding:"10px 12px", background:isFail?"var(--danger-bg)":isDue?"#FEF3E2":"transparent" }}>
            <div style={{ fontSize:14, fontWeight:500, color:isFail?"#8B1A14":v("text"), marginBottom:8 }}>{c.label}</div>
            {before?.[c.id] && (
              <div style={{ margin:"-4px -12px 4px" }}>
                <BeforeBox result={before[c.id].val === true ? "pass" : before[c.id].val === false ? "fail" : before[c.id].val === "due" ? "due" : null}>
                  {before[c.id].expiry && <span>· expiry {fmtDate(before[c.id].expiry)}</span>}
                </BeforeBox>
              </div>
            )}
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              {c.hasExpiry && (
                <label style={{ display:"flex", alignItems:"center", gap:6, flex:"1 1 180px", minWidth:170 }}>
                  <span style={{ fontSize:12, color:v("text-sec"), whiteSpace:"nowrap" }}>Expiry *</span>
                  <DateWheelField value={iso} allowClear placeholder="Pick expiry" style={{ flex:1, fontSize:14, padding:"8px 10px", minHeight:38 }}
                    onChange={val=>{
                      onUpdate(c.id,"expiry",val);
                      const auto = certStatusFromExpiry(val, offer(c)?.months || 1);   // picking a date sets Pass / Due / Fail
                      if (auto !== null) setResult(c, cur, auto, val);
                      else if (isFail) onFail("certs", c.id, failLabel(c, val), true, cur.note||"", cur.photos||[]);
                    }} />
                </label>
              )}
              <div style={{ display:"flex", gap:8 }}>
                <YnBtn active={cur.val===true} label="Pass" color={"#10B981"} onClick={()=>setResult(c, cur, cur.val===true?null:true)} />
                <YnBtn active={isDue}          label="Due"  color={"#E8A020"} onClick={()=>setResult(c, cur, isDue?null:"due")} />
                <YnBtn active={isFail}         label="Fail" color={"#EF4444"} onClick={()=>setResult(c, cur, isFail?null:false)} />
              </div>
            </div>
            {isDue && offer(c) && (
              <div style={{ marginTop:8, fontSize:13, fontWeight:600, color:"#7A4A08" }}>
                Expires within {offer(c).months} months, so it could run out during the rebuild. Added to Defects, so it's offered on the quote.
              </div>
            )}
            {isFail && (
              <FieldInput style={{ fontSize:13, padding:"7px 10px", marginTop:8 }} placeholder="What's wrong? e.g. expired, sticker missing…" defaultValue={cur.note||""}
                onBlur={e=>{ onUpdate(c.id,"note",e.target.value); onFail("certs", c.id, failLabel(c, cur.expiry), true, e.target.value, cur.photos||[]); }} />
            )}
            {isFail && <div style={{ margin:"8px -12px -10px" }}>
              <FailPhotoRow title="Fail photos" loc={{ t:"cert", id:c.id }}
                photos={cur.photos||[]}
                onAdd={src=>{ const ph=[...(cur.photos||[]), src]; onUpdate(c.id,"photos",ph); if(isFail) onFail("certs", c.id, failLabel(c, cur.expiry), true, cur.note||"", ph); }}
                onRemove={pi=>{ const ph=(cur.photos||[]).filter((_,j)=>j!==pi); onUpdate(c.id,"photos",ph); if(isFail) onFail("certs", c.id, failLabel(c, cur.expiry), true, cur.note||"", ph); }} />
            </div>}
          </div>
        );
      })}
    </SectionWrap>
  );
}

function ServiceActionsSection({ config, saState, onUpdate, onPhotoUpdate, oilSamplePhoto, collapsed, onToggle }) {
  return (
    <SectionWrap>
      <SectionHead title="Service Actions" collapsed={collapsed} onToggle={onToggle} badges={null} />
      {!collapsed && config.serviceActions.map(item => {
        const cur = saState?.[item.key];
        return (
          <div key={item.key} data-req={`s:${item.key}`}>
            <div style={{ padding:"16px 20px", borderTop:`1px solid ${v("border")}`, fontSize:14 }}>
              <div style={{ fontWeight:500, marginBottom:10, color:v("text") }}>{item.label}</div>
              <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                <SaBtn current={cur} value="done" label="Done" color={"#10B981"} onClick={()=>onUpdate(item.key, cur==="done"?null:"done")} />
                <SaBtn current={cur} value="na"   label="N/A"  color={"#94A3B8"}  onClick={()=>onUpdate(item.key, cur==="na"  ?null:"na")} />
                <SaBtn current={cur} value="skip" label="Skip" color={"#EF4444"}  onClick={()=>onUpdate(item.key, cur==="skip"?null:"skip")} />
                {item.litres    && <input inputMode="decimal" style={{ width:100, padding:"10px 12px" }} placeholder={item.showLitres?"Litres *":"Litres"} defaultValue={saState?.[item.litres]||""} onBlur={e=>onUpdate(item.litres,e.target.value)} />}
                {item.condition && <input style={{ width:140, padding:"10px 12px" }} placeholder="Condition…" defaultValue={saState?.[item.condition]||""} onBlur={e=>onUpdate(item.condition,e.target.value)} />}
              </div>
            </div>
            {item.hasPhoto && (
              <div style={{ padding:"16px 20px", borderTop:`1px solid ${v("border")}`, background:v("surface2") }}>
                <Label>Tracking label photo</Label>
                <div style={{ display:"flex", gap:12, marginTop:8, alignItems:"flex-start", flexWrap:"wrap" }}>
                  {oilSamplePhoto && (
                    <div style={{ position:"relative" }}>
                      <img src={oilSamplePhoto} alt="Tracking label" style={{ width:140, height:105, borderRadius:8, objectFit:"cover", border:`1px solid #10B981` }} />
                      <button onClick={()=>onPhotoUpdate(null)}
                        style={{ position:"absolute", top:-8, right:-8, background:"#DC2626", border:"none", borderRadius:"50%", width:24, height:24, cursor:"pointer", color:"#fff", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", padding:0 }}>✕</button>
                    </div>
                  )}
                  <div style={{ width:140 }}>
                    <InlinePhotoPicker onAdd={onPhotoUpdate} label={oilSamplePhoto?"Replace":"Add photo"} />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </SectionWrap>
  );
}

// ─── Readings (cycle times, pump test, deflection…) ───────────────────────────
// ─── Post-rebuild comparison ──────────────────────────────────────────────────
// Shaded, read-only box showing what the pre-rebuild found for an item
const BEFORE_RESULT = {
  pass: { text:"✓ Pass", color:"#059669" }, fail: { text:"✗ Fail", color:"#DC2626" },
  due:  { text:"Due",    color:"#B45309" }, na:   { text:"N/A",    color:"#64748B" },
};
function BeforeBox({ result, children, style }) {
  const r = BEFORE_RESULT[result];
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", margin:"2px 12px 8px", padding:"7px 10px",
      borderRadius:8, background:"rgba(30,95,168,0.08)", border:"1px solid rgba(30,95,168,0.18)", borderLeft:"4px solid #1E5FA8",
      fontSize:13, color:v("text-sec"), lineHeight:1.4, userSelect:"none", ...style }}>
      <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.08em", color:"#1E5FA8", textTransform:"uppercase" }}>Before rebuild</span>
      {result !== undefined && <span style={{ fontWeight:700, color:r ? r.color : v("text-ter") }}>{r ? r.text : "not checked"}</span>}
      {children}
    </div>
  );
}
// Before reading + change, flagged (amber) when it moved by more than 10%
function ReadingChange({ before, after, unit, judgedByRange }) {   // judgedByRange: the target range decides, not the % change
  const shownBefore = before === "na" ? "N/A" : before ? `${before} ${unit}` : "—";
  const raw = readingChange(before, after);
  const ch = raw && judgedByRange ? { ...raw, flag:false } : raw;
  const fmt = n => (n > 0 ? "+" : "") + (Math.round(n * 10) / 10);
  return (
    <BeforeBox style={{ flex:"1 1 100%", margin:"2px 0 0" }}>
      <span style={{ fontWeight:700, color:v("text") }}>{shownBefore}</span>
      {ch && (
        <span style={{ padding:"2px 8px", borderRadius:100, fontWeight:700,
          background:ch.flag?"#FEF3E2":"var(--surface2)", color:ch.flag?"#7A4A08":v("text-sec"), border:`1px solid ${ch.flag?"#E8A020":v("border")}` }}>
          {ch.flag ? "⚠ " : ""}{fmt(ch.diff)} {unit}{isFinite(ch.pct) ? ` (${fmt(ch.pct)}%)` : ""}
        </span>
      )}
    </BeforeBox>
  );
}
// Top of a Post-rebuild sheet: import the pre-rebuild (PDF or a sheet on this
// device), then show where it came from, flagged readings and its defects.
function RebuildImportPanel({ pre, measurements, measures, deviceSheets, onImportPdf, onImportSheet, onClear, collapsed, onToggle }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showDefects, setShowDefects] = useState(false);
  // Cycle times with a target range are judged against it; other readings flag on a >10% change
  const flagged = pre ? measurements.flatMap(g => g.items.map(it => ({ it, g }))).filter(({ it, g }) => {
    const range = g.targets ? (targetRange(pre.measures, it.key) || targetRange(measures, it.key)) : null;
    if (range) return ["low", "high"].includes(rangeStatus(measures?.[it.key], range));
    return readingChange(pre.measures?.[it.key], measures?.[it.key])?.flag;
  }).map(({ it }) => it) : [];
  return (
    <SectionWrap>
      <SectionHead title="Pre-rebuild comparison" collapsed={collapsed} onToggle={onToggle} badges={pre ? (
        flagged.length > 0
          ? <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, background:"#FEF3E2", color:"#7A4A08" }}>{flagged.length} flagged</span>
          : <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, background:"var(--success-bg)", color:"var(--success-text)" }}>Imported</span>
      ) : null} />
      {!collapsed && <div style={{ padding:"14px 16px", fontSize:14, color:v("text"), lineHeight:1.5 }}>
        {!pre ? (<>
          <p style={{ margin:"0 0 12px", color:v("text-sec") }}>Import the pre-rebuild sheet to see its results beside each item. Cycle times and pump readings are checked against the targets recorded on the pre-rebuild; deflection, and any reading without a target, is flagged when it changes by more than {REBUILD_FLAG_PCT}%.</p>
          <Btn variant="primary" disabled={busy} onClick={()=>fileRef.current?.click()}>{busy ? "Reading…" : "⬆ Import pre-rebuild PDF"}</Btn>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" style={{ display:"none" }}
            onChange={async e => { const f = e.target.files[0]; e.target.value = ""; if (!f) return; setBusy(true); try { await onImportPdf(f); } catch (err) { window.alert(err.message || "Couldn't read that PDF."); } setBusy(false); }} />
          {deviceSheets.length > 0 && (
            <div style={{ marginTop:14 }}>
              <Label>Or use a pre-rebuild sheet on this device</Label>
              {deviceSheets.map(sh => (
                <div key={sh.jobno} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderTop:`1px solid ${v("border")}` }}>
                  <span style={{ flex:1, fontSize:14 }}>{sh.header?.jobno || "Draft"}{sh.header?.rego ? ` · ${sh.header.rego}` : ""}{sh.header?.serial ? ` · S/N ${sh.header.serial}` : ""} <span style={{ color:v("text-sec"), fontSize:12 }}>· {new Date(sh.savedAt).toLocaleDateString("en-NZ")}</span></span>
                  <Btn variant="secondary" onClick={()=>onImportSheet(sh)}>Use</Btn>
                </div>
              ))}
            </div>
          )}
        </>) : (<>
          <div>Comparing with pre-rebuild <b>{pre.jobno || "(no job no.)"}</b>{pre.date ? ` · ${fmtDate(pre.date)}` : ""}{pre.tech ? ` · ${pre.tech}` : ""}</div>
          {flagged.length > 0 && <div style={{ marginTop:6, color:"#7A4A08" }}>⚠ Out of target range, or changed by more than {REBUILD_FLAG_PCT}%: {flagged.map(it => it.label).join(", ")}</div>}
          <div style={{ display:"flex", gap:8, marginTop:12, flexWrap:"wrap" }}>
            {pre.defects?.length > 0 && <Btn variant="secondary" onClick={()=>setShowDefects(s=>!s)}>{showDefects ? "Hide" : "Show"} defects found before ({pre.defects.length})</Btn>}
            <Btn variant="ghost" onClick={()=>{ if (window.confirm("Remove the imported pre-rebuild results from this sheet?")) onClear(); }}>Remove import</Btn>
          </div>
          {showDefects && (
            <ol style={{ margin:"12px 0 0", paddingLeft:20, color:v("text-sec"), fontSize:13 }}>
              {pre.defects.map((d, i) => <li key={i} style={{ marginBottom:4 }}>{d}</li>)}
            </ol>
          )}
        </>)}
      </div>}
    </SectionWrap>
  );
}

// A reading judged against its target range from the manual
function RangeChip({ status, unit }) {
  if (!status) return null;
  const secs = unit === "sec";
  const [text, ok] = status === "ok" ? ["✓ In range", true]
    : status === "low" ? [secs ? "✗ Too fast — below target" : "✗ Below target", false]
    : [secs ? "✗ Too slow — above target" : "✗ Above target", false];
  return (
    <span style={{ fontSize:12, fontWeight:700, padding:"2px 10px", borderRadius:100,
      background:ok?"var(--success-bg)":"var(--danger-bg)", color:ok?"var(--success-text)":"var(--danger-text)", border:`1px solid ${ok?"#10B981":"#EF4444"}` }}>{text}</span>
  );
}

function MeasurementsSection({ group, values, photos, onValue, onPhotos, collapsed, onToggle, before, requireTargets }) {
  const vals = values || {};
  const filled = group.items.filter(it => (vals[it.key]||"").toString().trim()).length;
  const ph = photos || [];
  const total = group.items.length + (group.photos?.required ? 1 : 0);
  const done = filled + (group.photos?.required && ph.length ? 1 : 0);
  return (
    <SectionWrap>
      <SectionHead title={group.title} collapsed={collapsed} onToggle={onToggle} badges={
        <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, background:done===total?"#E3F5EC":"#FEF3E2", color:done===total?"#145C33":"#7A4A08" }}>{done}/{total}</span>
      } />
      {!collapsed && <div>
        {group.note && <p style={{ fontSize:13, color:v("label"), margin:0, padding:"10px 12px 0" }}>{group.note}</p>}
        {group.items.map(it => {
          const val = vals[it.key] || "";
          const na = val === "na";
          // Targets come from the imported pre-rebuild when it has them, else are entered here
          const preRange = group.targets && before ? targetRange(before, it.key) : null;
          const range = group.targets ? (preRange || targetRange(vals, it.key)) : null;
          const cleanNum = s => s.replace(/[^\d.]/g, "");
          const tInput = { width:72, fontSize:14, padding:"7px 8px", textAlign:"right" };
          return (
            <div key={it.key} data-req={`m:${it.key}`} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderTop:`1px solid ${v("border")}`, fontSize:14, flexWrap:"wrap" }}>
              <span style={{ flex:"1 1 200px", color:v("text") }}>{it.label} *</span>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <FieldInput inputMode="decimal" disabled={na} style={{ width:110, fontSize:14, padding:"8px 10px", textAlign:"right", opacity:na?0.4:1 }}
                  placeholder={na?"N/A":"0"} defaultValue={na?"":val} key={na?"na":"v"}
                  onBlur={e=>onValue(it.key, e.target.value.replace(/[^\d.]/g,""))} />
                <span style={{ fontSize:13, color:v("text-sec"), width:40 }}>{it.unit}</span>
                {it.allowNA && <YnBtn active={na} label="N/A" color={"#94A3B8"} onClick={()=>onValue(it.key, na?"":"na")} />}
              </div>
              {group.targets && !na && (
                <div style={{ flex:"1 1 100%", display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", fontSize:13, color:v("text-sec") }}>
                  {preRange ? (
                    <span>{preRange.rec != null ? "Recommended" : "Target"} (from pre-rebuild): <b style={{ color:v("text") }}>{targetText(preRange, it.unit)}</b></span>
                  ) : group.targets === "recommended" ? (<>
                    {/* PTO pump test: one recommended figure, in range within ±REC_TOLERANCE_PCT */}
                    <span>Recommended (from manual){requireTargets ? " *" : ""}</span>
                    <FieldInput inputMode="decimal" placeholder="figure" style={{ ...tInput, width:90 }} defaultValue={vals[`${it.key}_rec`]||""}
                      onBlur={e=>onValue(`${it.key}_rec`, cleanNum(e.target.value))} />
                    <span>{it.unit}</span>
                    {range && <span>±{REC_TOLERANCE_PCT}% = {range.min}–{range.max}</span>}
                  </>) : (<>
                    <span>Target from manual{requireTargets ? " *" : ""}</span>
                    <FieldInput inputMode="decimal" placeholder="min" style={tInput} defaultValue={vals[`${it.key}_min`]||""}
                      onBlur={e=>onValue(`${it.key}_min`, cleanNum(e.target.value))} />
                    <span>–</span>
                    <FieldInput inputMode="decimal" placeholder="max" style={tInput} defaultValue={vals[`${it.key}_max`]||""}
                      onBlur={e=>onValue(`${it.key}_max`, cleanNum(e.target.value))} />
                    <span>{it.unit}</span>
                  </>)}
                  <RangeChip status={rangeStatus(val, range)} unit={it.unit} />
                </div>
              )}
              {before && <ReadingChange before={before[it.key]} after={val} unit={it.unit} judgedByRange={!!range} />}
            </div>
          );
        })}
        {group.photos && (
          <div data-req={`mp:${group.id}`} style={{ margin:"0" }}>
            <FailPhotoRow title={`${group.photos.label}${group.photos.required?" *":""}`} color="#1E5FA8" photos={ph} loc={{ t:"measure", id:group.id }}
              onAdd={src=>onPhotos([...ph, src])} onRemove={pi=>onPhotos(ph.filter((_,j)=>j!==pi))} />
          </div>
        )}
      </div>}
    </SectionWrap>
  );
}

const OPERATIONAL_FIELDS = [
  {label:"Blower — vacuum (inHg)",            key:"blowerVac"},
  {label:"Blower — pressure (psi)",            key:"blowerPsi"},
  {label:"Water pump — unloader relief (psi)", key:"wpUnloader"},
];
function OperationalChecksSection({ secState, onUpdate, collapsed, onToggle }) {
  const fields = OPERATIONAL_FIELDS;
  return (
    <SectionWrap>
      <SectionHead title="Operational Checks" collapsed={collapsed} onToggle={onToggle} badges={null} />
      {!collapsed && <div>
        {fields.map(f => (
          <div key={f.key} data-req={`o:${f.key}`} style={{ padding:"12px", borderTop:`1px solid ${v("border")}`, fontSize:14 }}>
            <div style={{ fontWeight:500, marginBottom:6 }}>{f.label}</div>
            <FieldInput style={{ fontSize:13, padding:"8px 10px" }} placeholder="Reading…" defaultValue={secState?.[f.key]||""} onBlur={e=>onUpdate(f.key,e.target.value)} />
          </div>
        ))}
      </div>}
    </SectionWrap>
  );
}

// ─── Rectification photo field ────────────────────────────────────────────────
function RectPhotoField({ photos, onAdd, onRemove, onLightbox, prefix }) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:8 }}>
      {photos.map((src,pi) => (
        <div key={pi} style={{ position:"relative" }}>
          <img src={src} alt="Rect photo" onClick={()=>onLightbox(src)}
            style={{ width:"100%", aspectRatio:"4/3", borderRadius:8, objectFit:"cover", border:`1px solid #10B981`, cursor:"pointer", display:"block" }} />
          <button onClick={()=>onRemove(pi)}
            style={{ position:"absolute", top:-8, right:-8, background:"#DC2626", border:"none", borderRadius:"50%", width:24, height:24, cursor:"pointer", color:"#fff", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", padding:0 }}>✕</button>
          <a href={src} download={`${prefix}-${pi+1}.jpg`}
            style={{ position:"absolute", bottom:6, right:6, background:"rgba(0,0,0,0.6)", borderRadius:4, padding:"3px 7px", fontSize:11, color:"#fff", textDecoration:"none" }}
            onClick={e=>e.stopPropagation()}>⬇</a>
        </div>
      ))}
      <InlinePhotoPicker onAdd={onAdd} label="Add rect. photo" />
    </div>
  );
}

// ─── Defects — only shows fails, no empty rows ────────────────────────────────
function DefectsSection({ defects, onUpdate, collapsed, onToggle, hideRect }) {
  const [lightbox, setLightbox] = useState(null);
  const fileRefs = useRef({});
  const count = defects.filter(d=>d.defect).length;

  return (
    <SectionWrap>
      <SectionHead title="Defects & Rectifications" collapsed={collapsed} onToggle={onToggle} badges={
        <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, background:count>0?"#FAECE7":"#f0f0ee", color:count>0?"#8B1A14":v("text-sec") }}>
          {count>0 ? `${count} defect${count>1?"s":""}` : "No defects"}
        </span>
      } />
      {!collapsed && (
        count === 0 ? (
          <div style={{ padding:"24px", textAlign:"center", color:v("text-ter"), fontSize:14 }}>
            ✓ No fails recorded — defects will appear here automatically when items are failed above.
          </div>
        ) : (
          defects.filter(d=>d.defect).map((defect, idx) => (
            <div key={defect.failKey||idx} style={{ borderTop:`1px solid ${v("border")}`, padding:"14px 12px", background:"var(--surface)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                <span style={{ fontSize:13, fontWeight:700, background:"#EF4444", color:"#fff", borderRadius:100, padding:"2px 10px" }}>#{idx+1}</span>
                <span style={{ fontSize:12, background:"var(--danger-bg)", color:"var(--danger-text)", borderRadius:100, padding:"1px 8px", fontWeight:600 }}>From inspection</span>
                <button onClick={()=>onUpdate(defect.failKey,"sendToZendesk",defect.sendToZendesk===false?true:false)}
                  style={{ marginLeft:"auto", fontSize:12, padding:"4px 12px", borderRadius:8, border:`1px solid ${defect.sendToZendesk!==false?"#10B981":v("border")}`, background:defect.sendToZendesk!==false?"#E3F5EC":"transparent", color:defect.sendToZendesk!==false?"#145C33":v("text-sec"), cursor:"pointer" }}>
                  {defect.sendToZendesk!==false ? "✓ Send to Zendesk" : "○ Don't send"}
                </button>
              </div>
              <div style={{ marginBottom:10 }}>
                <Label>Defect</Label>
                <div style={{ fontSize:14, color:v("text"), padding:"8px 0" }}>{defect.defect}</div>
              </div>
              {defect.photos?.length>0 && (
                <div style={{ marginBottom:16 }}>
                  <Label>Fail photos</Label>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:8 }}>
                    {defect.photos.map((src,pi) => (
                      <div key={pi} style={{ position:"relative" }}>
                        <img src={src} alt={`Fail photo ${pi+1}`} onClick={()=>setLightbox(src)}
                          style={{ width:"100%", aspectRatio:"4/3", borderRadius:8, objectFit:"cover", border:`1px solid var(--danger-border)`, cursor:"pointer", display:"block" }} />
                        <button onClick={()=>onUpdate(defect.failKey,"photos",(defect.photos||[]).filter((_,j)=>j!==pi))}
                          style={{ position:"absolute", top:-8, right:-8, background:"#DC2626", border:"none", borderRadius:"50%", width:24, height:24, cursor:"pointer", color:"#fff", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", padding:0 }}>✕</button>
                        <a href={src} download={`defect-${idx+1}-photo-${pi+1}.jpg`}
                          style={{ position:"absolute", bottom:6, right:6, background:"rgba(0,0,0,0.6)", borderRadius:4, padding:"3px 7px", fontSize:11, color:"#fff", textDecoration:"none" }}
                          onClick={e=>e.stopPropagation()}>⬇</a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
{!hideRect && (<>
              <div>
                <Label>Rectification</Label>
                <textarea style={{ width:"100%", resize:"vertical", minHeight:88, lineHeight:1.5 }}
                  placeholder="Describe rectification…" defaultValue={defect.rect||""}
                  onBlur={e=>onUpdate(defect.failKey, "rect", e.target.value)} />
              </div>
              <div style={{ marginTop:10 }}>
                <Label>Rectification photo</Label>
                <RectPhotoField
                  photos={defect.rectPhotos||[]}
                  onAdd={src=>onUpdate(defect.failKey,"rectPhotos",[...(defect.rectPhotos||[]),src])}
                  onRemove={pi=>onUpdate(defect.failKey,"rectPhotos",(defect.rectPhotos||[]).filter((_,j)=>j!==pi))}
                  onLightbox={setLightbox}
                  prefix={`defect-${idx+1}-rect`}
                  color={"#10B981"}
                />
              </div>
</>)}
              <div style={{ marginTop:10 }}>
                <Label>Recommendation</Label>
                <textarea style={{ width:"100%", resize:"vertical", minHeight:88, lineHeight:1.5 }}
                  placeholder="Any recommendations for future action…" defaultValue={defect.recommendation||""}
                  onBlur={e=>onUpdate(defect.failKey, "recommendation", e.target.value)} />
              </div>
            </div>
          ))
        )
      )}
      {lightbox && (
        <div onClick={()=>setLightbox(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.9)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <img src={lightbox} alt="Defect" style={{ maxWidth:"92vw", maxHeight:"88vh", borderRadius:12, objectFit:"contain" }} />
          <button onClick={()=>setLightbox(null)} style={{ position:"absolute", top:16, right:16, background:v("surface"), border:"none", borderRadius:"50%", width:40, height:40, cursor:"pointer", fontSize:20 }}>✕</button>
        </div>
      )}
    </SectionWrap>
  );
}

// ─── Other Notes section ──────────────────────────────────────────────────────
function OtherNotesSection({ notes, onUpdate, defectCount, collapsed, onToggle, hideRect }) {
  
  const [lightbox, setLightbox] = useState(null);
  const filled = notes.filter(n=>n.text||n.photos?.length>0).length;
  return (
    <SectionWrap>
      <SectionHead title="Other Notes" collapsed={collapsed} onToggle={onToggle} badges={
        filled>0 ? <span style={{ fontSize:12, padding:"3px 10px", borderRadius:100, fontWeight:600, background:"var(--info-bg)", color:"var(--info-text)" }}>{filled} note{filled>1?"s":""}</span> : null
      } />
      {!collapsed && (
        <div>
          {notes.map((note, idx) => {
            const globalNum = defectCount + idx + 1;
            const isLast = idx === notes.length - 1;
            return (
              <div key={idx} style={{ borderTop: idx>0?`1px solid ${v("border")}`:"none", padding:"14px 12px", background:"var(--surface)" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                  <span style={{ fontSize:13, fontWeight:700, background:"#1E5FA8", color:"#fff", borderRadius:100, padding:"2px 10px" }}>#{globalNum}</span>
                  <button onClick={()=>onUpdate(idx,"sendToZendesk",note.sendToZendesk===false?true:false)}
                    style={{ marginLeft:"auto", fontSize:12, padding:"4px 12px", borderRadius:8, border:`1px solid ${note.sendToZendesk!==false?"#10B981":v("border")}`, background:note.sendToZendesk!==false?"#E3F5EC":"transparent", color:note.sendToZendesk!==false?"#145C33":v("text-sec"), cursor:"pointer" }}>
                    {note.sendToZendesk!==false ? "✓ Send to Zendesk" : "○ Don't send"}
                  </button>
                </div>
                <div style={{ marginBottom:12 }}>
                  <Label>Note</Label>
                  <textarea style={{ width:"100%", resize:"vertical", minHeight:88, lineHeight:1.5 }}
                    placeholder="Additional notes, observations or comments…"
                    defaultValue={note.text}
                    onBlur={e=>{ onUpdate(idx,"text",e.target.value); if(isLast && e.target.value.trim()) onUpdate("addNew",null); }} />
                </div>
                <Label>Photos</Label>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:8 }}>
                  {note.photos.map((src,pi) => (
                    <div key={pi} style={{ position:"relative" }}>
                      <img src={src} alt="Note photo" onClick={()=>setLightbox(src)}
                        style={{ width:"100%", aspectRatio:"4/3", borderRadius:8, objectFit:"cover", border:`1px solid ${v("border")}`, cursor:"pointer", display:"block" }} />
                      <button onClick={()=>onUpdate(idx,"photos",(note.photos||[]).filter((_,j)=>j!==pi))}
                        style={{ position:"absolute", top:-8, right:-8, background:"#DC2626", border:"none", borderRadius:"50%", width:24, height:24, cursor:"pointer", color:"#fff", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", padding:0 }}>✕</button>
                      <a href={src} download={`note-${idx+1}-photo-${pi+1}.jpg`}
                        style={{ position:"absolute", bottom:6, right:6, background:"rgba(0,0,0,0.6)", borderRadius:4, padding:"3px 7px", fontSize:11, color:"#fff", textDecoration:"none" }}
                        onClick={e=>e.stopPropagation()}>⬇</a>
                    </div>
                  ))}
                  <InlinePhotoPicker onAdd={src=>onUpdate(idx,"photo",src)} label="Add photo" />
                </div>
{!hideRect && (<>
                <div style={{ marginTop:12 }}>
                  <Label>Rectification</Label>
                  <textarea style={{ width:"100%", resize:"vertical", minHeight:88, lineHeight:1.5 }}
                    placeholder="Describe rectification…" defaultValue={note.rect||""}
                    onBlur={e=>onUpdate(idx,"rect",e.target.value)} />
                </div>
                <div style={{ marginTop:10 }}>
                  <Label>Rectification photo</Label>
                  <RectPhotoField
                    photos={note.rectPhotos||[]}
                    onAdd={src=>onUpdate(idx,"rectPhotos",[...(note.rectPhotos||[]),src])}
                    onRemove={pi=>onUpdate(idx,"rectPhotos",(note.rectPhotos||[]).filter((_,j)=>j!==pi))}
                    onLightbox={setLightbox}
                    prefix={`note-${idx+1}-rect`}
                    color={"#10B981"}
                  />
                </div>
</>)}
                <div style={{ marginTop:10 }}>
                  <Label>Recommendation</Label>
                  <textarea style={{ width:"100%", resize:"vertical", minHeight:88, lineHeight:1.5 }}
                    placeholder="Any recommendations for future action…" defaultValue={note.recommendation||""}
                    onBlur={e=>onUpdate(idx,"recommendation",e.target.value)} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {lightbox && (
        <div onClick={()=>setLightbox(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.9)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <img src={lightbox} alt="Note" style={{ maxWidth:"92vw", maxHeight:"88vh", borderRadius:12, objectFit:"contain" }} />
          <button onClick={()=>setLightbox(null)} style={{ position:"absolute", top:16, right:16, background:v("surface"), border:"none", borderRadius:"50%", width:40, height:40, cursor:"pointer", fontSize:20 }}>✕</button>
        </div>
      )}
    </SectionWrap>
  );
}

// ─── Save photos to the phone ─────────────────────────────────────────────────
// Photos taken in the app live only inside the app. This hands every photo on
// the sheet to the phone's share sheet ("Save images" puts them in the camera
// roll), or downloads them one by one where sharing files isn't supported.
function collectPhotos(state) {
  const out = [], seen = new Set();
  const walk = x => {
    if (typeof x === "string") { if (x.startsWith("data:image/jpeg") && !seen.has(x)) { seen.add(x); out.push(x); } }
    else if (Array.isArray(x)) x.forEach(walk);
    else if (x && typeof x === "object") Object.values(x).forEach(walk);
  };
  walk(state);
  return out;
}
async function savePhotosToDevice(state, header) {
  const photos = collectPhotos(state);
  if (!photos.length) { window.alert("No photos on this sheet yet."); return; }
  const base = (header.jobno || header.rego || "checksheet").replace(/[^\w-]+/g, "_");
  const files = await Promise.all(photos.map((src, i) => fetch(src).then(r => r.blob())
    .then(b => new File([b], `${base}-photo-${i + 1}.jpg`, { type: "image/jpeg" }))));
  if (navigator.canShare && navigator.canShare({ files })) {
    try { await navigator.share({ files, title: `${base} photos` }); return; }
    catch (e) { if (e.name === "AbortError") return; }
  }
  files.forEach((f, i) => setTimeout(() => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(f); a.download = f.name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  }, i * 300));
}

// ─── Selector screen ──────────────────────────────────────────────────────────
function SelectorScreen({ onSelect, children }) {  // children (saved sheets) sit under the picker
  const [equip, setEquip] = useState(null);
  const [model, setModel] = useState(null);   // null = no model picked yet
  const chooseEquip = (id) => { setEquip(id); setModel(null); };
  const modelOpts = equip && MODEL_OPTIONS[equip] ? MODEL_OPTIONS[equip].filter(m => !m.hidden) : null;
  const modelLabel = modelOpts && model!==null ? (modelOpts.find(m=>m.id===model)?.label || "") : null;
  // Service levels only appear once a model is picked (types without models skip straight to them)
  const showService = equip && (!modelOpts || model!==null);

  return (
    <div style={{ minHeight:"100vh", background:v("bg") }}>
      <div style={{ background:"#1E5FA8", padding:"0 1.5rem", height:56, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <img src={LOGO_B64} alt="Waimea Group" style={{ height:34 }} />
        <span style={{ fontSize:12, color:"rgba(255,255,255,0.75)", letterSpacing:"0.05em", textTransform:"uppercase" }}>Service Checksheets</span>
      </div>
      <div style={{ maxWidth:540, margin:"0 auto", padding:"2rem 1rem" }}>
        <div style={{ textAlign:"center", marginBottom:"2rem" }}>
          <h1 style={{ fontSize:24, fontWeight:700, color:"var(--info-text)", margin:"0 0 8px" }}>Select Checksheet</h1>
          <p style={{ fontSize:14, color:v("text-sec"), margin:0 }}>Pick the equipment, model and service level to start <span style={{ opacity:0.6 }}>· {APP_VERSION}</span></p>
        </div>
        <div style={{ background:v("surface"), border:`0.5px solid ${v("border")}`, borderRadius:12, padding:"1.5rem", marginBottom:"1rem", boxShadow:"0 1px 4px rgba(0,0,0,0.07)" }}>
          <Label>Equipment type</Label>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:12 }}>
            {EQUIPMENT_TYPES.map(e => {
              const isSelected = equip===e.id;
              return (
                <button key={e.id} onClick={()=>chooseEquip(e.id)} style={{ padding:"8px 8px 12px", borderRadius:12, border:isSelected?`2.5px solid ${"#1E5FA8"}`:`1px solid ${v("border")}`, background:isSelected?"#EBF2FA":v("surface"), cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:10, transition:"all 0.15s", boxShadow:isSelected?`0 0 0 4px rgba(30,95,168,0.1)`:"none" }}>
                  <BlueprintPanel active={isSelected}><e.Icon size={170} /></BlueprintPanel>
                  <span style={{ fontSize:16, fontWeight:600, color:isSelected?"#1E5FA8":v("text") }}>{e.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        {equip && modelOpts && (
          <div style={{ background:v("surface"), border:`0.5px solid ${v("border")}`, borderRadius:12, padding:"1.5rem", marginBottom:"1rem", boxShadow:"0 1px 4px rgba(0,0,0,0.07)" }}>
            <Label>Model</Label>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
              {modelOpts.map(m => {
                const isSelected = model===m.id;
                return (
                  <button key={m.id||"generic"} onClick={()=>setModel(m.id)}
                    style={{ padding:"14px 12px", borderRadius:10, border:isSelected?`2px solid #1E5FA8`:`1px solid ${v("border")}`, background:isSelected?"#EBF2FA":v("surface"), cursor:"pointer", fontSize:13, fontWeight:600, color:isSelected?"#1E5FA8":v("text"), transition:"all 0.15s" }}>
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {showService && (
          <div style={{ background:v("surface"), border:`0.5px solid ${v("border")}`, borderRadius:12, padding:"1.5rem", boxShadow:"0 1px 4px rgba(0,0,0,0.07)" }}>
            <Label>Service level{modelOpts ? ` — ${modelLabel}` : ""}</Label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:12, marginTop:12 }}>
              {(equip==="ewp" ? ["A","B","OH","PH","PR","PO"] : ["A","B","OH","PH"]).map(svc => (
                <button key={svc} onClick={()=>onSelect(equip, model||"", svc.toLowerCase())}
                  style={{ padding:"22px 14px", borderRadius:12, border:`1px solid ${v("border")}`, background:v("surface"), cursor:"pointer", transition:"all 0.15s" }}
                  onMouseOver={e=>{ e.currentTarget.style.background="#EBF2FA"; e.currentTarget.style.borderColor="#1E5FA8"; }}
                  onMouseOut={e=>{ e.currentTarget.style.background=v("surface"); e.currentTarget.style.borderColor=v("border"); }}>
                  <div style={{ fontSize:22, fontWeight:700, color:"var(--info-text)", marginBottom:6 }}>{serviceLabel(svc)}</div>
                  <div style={{ fontSize:13, color:v("text-sec") }}>{svc==="A"?"Filters, lube & inspection":svc==="B"?"Oil change + full service":svc==="OH"?"Inspection, grease/lube & refuel":svc==="PH"?"Unit sitting idle – key checks, refuel & wash":svc==="PR"?"Compliance inspection before a rebuild – tests & cycle times":"Re-check after a rebuild – compared with the pre-rebuild"}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {children}
        <p style={{ textAlign:"center", fontSize:12, color:v("text-ter"), marginTop:"2rem" }}>Waimea Group · Specialist Utility Vehicles · waimea.co.nz</p>
      </div>
    </div>
  );
}

const FIELD_LABELS = {
  jobno:"Job no.", date:"Date", tech:"Technician",
  make:"Make", model:"Model", serial:"Serial no.",
  tankerModel:"Tanker make & model", tankerSerial:"Tanker serial no.",
  rego:"Vehicle rego", pto:"PTO / unit hours", nextsvc:"Next service due",
  odo:"Odometer (km)", enghours:"Vehicle hours",
  customer:"Customer company", contact:"Service contact",
};

// v40 tidied the checklists (some items removed, merged or moved section). Answers
// on a sheet started before that are moved to the matching rows by item label, so
// an in-progress sheet doesn't end up with answers against the wrong checks.
const LABEL_RENAMES = {
  "Control decals": "Control decals — upper & lower",
  "Lower control decals": "Control decals — upper & lower",
  "Oil level & condition": "Hydraulic oil level & condition",
  "Battery check": "Battery — condition & charge",
};
// v43 merged the paired Pre-rebuild functional checks (CW + CCW, raise + lower,
// extend + retract) into one line each. Old answers combine: Fail wins, then Pass.
const PR42_FUNCTIONAL = ["Jacks & feet","Outriggers & spring locks","Interlock — no boom operation unless jacks set","Interlock — no jack operation unless boom stowed",
  "Boom raise","Boom lower","Boom extend","Boom retract","Boom rotation CW","Boom rotation CCW","Bucket raise / lower","Bucket rotation CW","Bucket rotation CCW",
  "Arm rotation CW","Arm rotation CCW","Winch raise / lower","Winch pay in / out","Winch rotation CW / CCW"];
const PR42_MERGE = { "Boom raise":"Boom raise / lower","Boom lower":"Boom raise / lower","Boom extend":"Boom extend / retract","Boom retract":"Boom extend / retract",
  "Boom rotation CW":"Boom rotation — CW & CCW","Boom rotation CCW":"Boom rotation — CW & CCW","Bucket rotation CW":"Bucket rotation — CW & CCW","Bucket rotation CCW":"Bucket rotation — CW & CCW",
  "Arm rotation CW":"Arm rotation — CW & CCW","Arm rotation CCW":"Arm rotation — CW & CCW","Winch rotation CW / CCW":"Winch rotation — CW & CCW" };
function migratePreRebuild(sheet, st) {
  if (sheet.configKey !== "ewp-pr" || (st._pr||0) >= 43 || st.completedAt) return st;
  const oldF = st.functional || {}, items = getSectionsForModel("ewp-pr").find(s => s.id === "functional").items;
  // old list = first 18 changed rows, then the unchanged tail in the same order
  const tail = items.slice(items.indexOf("Tool port operation"), items.indexOf("Hydraulic cooling fan — operation"));   // items added since v43 aren't in old sheets
  const oldList = [...PR42_FUNCTIONAL, ...tail];
  if (Object.keys(oldF).length !== oldList.length) return { ...st, _pr:43 };
  const rank = r => r?.status === "fail" ? 3 : r?.status === "pass" ? 2 : r?.status === "na" ? 1 : 0;
  const fresh = {}, keyMap = {};
  items.forEach((lab, i) => { fresh[i] = { status:null, note:"", photos:[] }; });
  oldList.forEach((lab, oi) => {
    const ni = items.indexOf(PR42_MERGE[lab] || lab); if (ni < 0) return;
    const r = oldF[oi]; if (!r) return;
    const cur = fresh[ni];
    if (rank(r) > rank(cur)) fresh[ni] = { ...r, note: [cur.note, r.note].filter(Boolean).join("; "), photos: [...(cur.photos||[]), ...(r.photos||[])] };
    else if (r.note || (r.photos||[]).length) fresh[ni] = { ...cur, note: [cur.note, r.note].filter(Boolean).join("; "), photos: [...(cur.photos||[]), ...(r.photos||[])] };
    keyMap[`functional-${oi}`] = `functional-${ni}`;
  });
  return { ...st, _pr:43, functional: fresh, defects: (st.defects||[]).map(d => keyMap[d.failKey] ? { ...d, failKey: keyMap[d.failKey] } : d) };
}
function migrateSheetState(sheet) {
  const st0 = migrateLegacy(sheet);
  return st0 ? migratePreRebuild(sheet, st0) : st0;
}
function migrateLegacy(sheet) {
  const st = sheet.state;
  if (!st || st._v >= 40 || st.completedAt) return st;
  const old = LEGACY_V39[sheet.configKey];
  if (!old) return { ...st, _v:40 };
  const sections = getSectionsForModel(sheet.configKey, sheet.header?.modelVariant);
  const where = {};   // label -> list of old {sec, idx}
  Object.entries(old).forEach(([sid, items]) => items.forEach((lab, idx) => {
    [lab, LABEL_RENAMES[lab]].filter(Boolean).forEach(l => (where[l] = where[l] || []).push({ sid, idx }));
  }));
  const next = { ...st, _v:40 }, keyMap = {}, used = new Set();
  sections.forEach(sec => {
    if (!old[sec.id]) return;                       // model extras etc. — unchanged
    const fresh = {};
    sec.items.forEach((lab, i) => {
      const cands = where[lab] || [];
      const hit = cands.find(c => c.sid === sec.id && !used.has(`${c.sid}-${c.idx}`)) || cands.find(c => !used.has(`${c.sid}-${c.idx}`));
      if (hit && st[hit.sid]?.[hit.idx]) { used.add(`${hit.sid}-${hit.idx}`); fresh[i] = st[hit.sid][hit.idx]; keyMap[`${hit.sid}-${hit.idx}`] = `${sec.id}-${i}`; }
      else fresh[i] = { status:null, note:"", photos:[] };
    });
    next[sec.id] = fresh;
  });
  next.defects = (st.defects || []).map(d => keyMap[d.failKey] ? { ...d, failKey: keyMap[d.failKey] } : d);
  return next;
}

// Completed sheets are kept on the device for 14 days (to re-download the PDF),
// then removed — so old sheets can't be picked up and reused for another unit.
const KEEP_COMPLETED_DAYS = 14;
function cleanupCompleted() {
  const all = getAllSheets();
  const cutoff = Date.now() - KEEP_COMPLETED_DAYS * 86400000;
  all.forEach(sh => { const c = sh.state?.completedAt; if (c && new Date(c).getTime() < cutoff) deleteSheet(sh.jobno); });
  return getAllSheets();
}

// ─── Version / update check ───────────────────────────────────────────────────
// public/version.json is updated with every release. If the server has a newer
// version than the one running, show "Tap to update" (sheets are saved first).
export const APP_VERSION = "v69";
function useUpdateAvailable() {
  const [newVer, setNewVer] = useState(null);
  useEffect(() => {
    let stop = false;
    const check = () => {
      if (!navigator.onLine) return;
      fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (!stop && d && d.version && d.version !== APP_VERSION) setNewVer(d.version); })
        .catch(() => {});
    };
    check();
    const t = setInterval(check, 5 * 60 * 1000);
    const vis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("online", check);
    return () => { stop = true; clearInterval(t); document.removeEventListener("visibilitychange", vis); window.removeEventListener("online", check); };
  }, []);
  return newVer;
}
export function UpdateRoot() { return <UpdateBanner version={useUpdateAvailable()} />; }
function UpdateBanner({ version }) {
  if (!version) return null;
  return (
    <button onClick={() => { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); setTimeout(() => flushSaves().finally(() => window.location.reload()), 400); }}
      style={{ position:"fixed", left:"50%", bottom:"calc(16px + env(safe-area-inset-bottom, 0px))", transform:"translateX(-50%)", zIndex:1000,
        background:"#1E5FA8", color:"#fff", border:"none", borderRadius:100, padding:"12px 20px", fontSize:14, fontWeight:700,
        boxShadow:"0 6px 20px rgba(0,0,0,0.25)", maxWidth:"calc(100% - 32px)" }}>
      ⟳ New version ({version}) available — tap to update
    </button>
  );
}

// Make/model filled in from the model picked at the start, so techs don't retype them.
// Exact models (AV7000H, PK19…) fill both; make-level picks (Versalift, Donghae) fill the make.
function modelHeader(modelId) {
  const m = findModelOption(modelId);
  if (!m) return { make:"", model:"", tankerModel:"" };
  if (m.exact) return { make: m.make || "", model: m.label, tankerModel: m.label };
  return { make: m.make || m.label, model:"", tankerModel:"" };
}

// ─── Required-field check ─────────────────────────────────────────────────────
// Everything a tech must complete before the PDF can be downloaded. Returns the
// missing items in on-screen order; each has the data-req id of the element to
// jump to and the collapse key of the section it sits in.
function headerFieldsFor(config, header) {
  const opt = findModelOption(header.modelVariant);
  const noMakeModel = opt && opt.exact, noMake = opt && !opt.exact;
  const eq = (config.equipmentFields || []).filter(f => !(f === "make" && (noMake || noMakeModel)) && !((f === "model" || f === "tankerModel") && noMakeModel));
  const vf = config.vehicleFields || ["rego","pto","nextsvc"];
  const job = ["jobno","date","tech", ...((config.service==="OH"||config.service==="PH") ? [] : ["customer","contact"])];
  return { job, eq, vf };
}
const OPTIONAL_FIELDS = new Set(["nextsvc","contact"]);
export function fmtDate(iso) { // "2026-10-12" -> "12/10/2026"; anything else shown as typed
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || ""); return m ? `${m[3]}/${m[2]}/${m[1]}` : (iso || "");
}
// Every required item on the sheet, in on-screen order, with done = true/false.
function collectRequired(config, eff, header, state) {
  const all = [];
  const add = (req, sec, label, done) => all.push({ req, sec, label, done: !!done });
  const { job, eq, vf } = headerFieldsFor(config, header);
  [...job, ...eq, ...vf].forEach(f => {
    if (OPTIONAL_FIELDS.has(f)) return;
    const val = (header[f] || "").toString().trim();
    add(`h:${f}`, null, f==="jobno" ? "Job number (digits after J00)" : (FIELD_LABELS[f] || f), f === "jobno" ? /^J00\d+$/.test(val) : !!val);
  });
  (config.certs || []).forEach(c => {
    const cur = state.certs?.[c.id] || {};
    if (c.hasExpiry) add(`c:${c.id}`, "certs", `Certs — ${c.label}: expiry date`, (cur.expiry||"").trim());
    add(`c:${c.id}`, "certs", `Certs — ${c.label}: pass, due or fail`, isCertAnswered(cur.val));
  });
  VEHICLE_PHOTO_SLOTS.forEach(s => add(`p:${s.key}`, "vehiclePhotos", `Vehicle photo — ${s.label}`, state.vehiclePhotos?.[s.key]));
  (eff.hirePhotos || []).forEach(s => add(`hp:${s.key}`, "hirePhotos", `Condition photo — ${s.label}`, hirePhotoList(state, s.key).length));
  eff.sections.forEach(sec => sec.items.forEach((label, i) => {
    const it = state[sec.id]?.[i];
    add(`i:${sec.id}:${i}`, sec.id, `${sec.title} — ${label}`, it?.status);
  }));
  if (config.operationalChecks) OPERATIONAL_FIELDS.forEach(f => add(`o:${f.key}`, "operational", `Operational — ${f.label}`, (state.operationalChecks?.[f.key]||"").toString().trim()));
  (eff.serviceActions || []).forEach(item => {
    const cur = state.serviceActions?.[item.key];
    add(`s:${item.key}`, "service", `Service action — ${item.label}`, cur);
    if (item.showLitres) add(`s:${item.key}`, "service", `${item.label}: litres`, cur && cur !== "done" ? true : (state.serviceActions?.[item.litres]||"").toString().trim());
    if (item.hasPhoto) add(`s:${item.key}`, "service", `${item.label}: tracking label photo`, cur && cur !== "done" ? true : state.oilSamplePhoto);
  });
  (config.measurements || []).forEach(g => {
    g.items.forEach(it => {
      const val = (state.measures?.[it.key]||"").toString().trim();
      add(`m:${it.key}`, `m-${g.id}`, `${g.title} — ${it.label}`, val);
      // Pre-rebuild: target min / max from the manual for every movement the unit has
      if (g.targets === "recommended" && config.service === "PR" && val !== "na") {
        add(`m:${it.key}`, `m-${g.id}`, `${g.title} — ${it.label}: recommended figure`, (state.measures?.[`${it.key}_rec`]||"").toString().trim());
      } else if (g.targets && config.service === "PR" && val !== "na") {
        add(`m:${it.key}`, `m-${g.id}`, `${g.title} — ${it.label}: target min`, (state.measures?.[`${it.key}_min`]||"").toString().trim());
        add(`m:${it.key}`, `m-${g.id}`, `${g.title} — ${it.label}: target max`, (state.measures?.[`${it.key}_max`]||"").toString().trim());
      }
    });
    if (g.photos?.required) add(`mp:${g.id}`, `m-${g.id}`, `${g.title} — ${g.photos.label.toLowerCase()}`, (state.measurePhotos?.[g.id]||[]).length);
  });
  add("sig", null, "Technician signature", state.signature);
  if (config.teamLeaderSignoff) add("tlsig", null, "Team leader sign-off", state.tlSignature);
  return all;
}
function findMissing(config, eff, header, state) { return collectRequired(config, eff, header, state).filter(x => !x.done); }

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function App() {
  const [dark, setDark]           = useState(getDarkMode);
  C = makeColors(dark);
  // Apply CSS vars to document root
  useEffect(() => { applyTheme(dark); }, [dark]);
  // Highlight used when jumping to an incomplete required item
  useEffect(() => {
    if (document.getElementById("req-flash-css")) return;
    const st = document.createElement("style"); st.id = "req-flash-css";
    st.textContent = "@keyframes reqFlash{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0)}25%,65%{box-shadow:0 0 0 4px rgba(239,68,68,.9)}}" +
      ".req-flash{animation:reqFlash 1.1s ease 2;border-radius:8px}";
    document.head.appendChild(st);
  }, []);
  const toggleDark = () => { const d = !dark; setDark(d); setDarkMode(d); applyTheme(d); };

  // ── Safety net: force any focused field to commit its value before we lose
  // the tab/app — this is what actually prevents "lost my last note" reports.
  // Fields save on blur; if someone locks their phone or swipes away without
  // tapping out of a field first, that text never reaches state. Blurring the
  // active element on hide/close fires the field's own onBlur save handler.
  useEffect(() => {
    const forceCommit = () => {
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
      }
    };
    const onVisibility = () => { if (document.visibilityState === "hidden") forceCommit(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", forceCommit);
    window.addEventListener("blur", forceCommit);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", forceCommit);
      window.removeEventListener("blur", forceCommit);
    };
  }, []);


  const [screen, setScreen]       = useState("home"); // "home" | "sheet"
  const [configKey, setConfigKey] = useState(null);
  const [state, setState]         = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [saved, setSaved]         = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [autoSave, setAutoSave]   = useState(getAutoSavePhotos);
  const [moving, setMoving]       = useState(null);   // { loc, src } while the Move / Copy sheet is open
  const [saveFailing, setSaveFailing] = useState(() => !lastSaveOk());   // device storage write failed
  useEffect(() => onSaveStatus(({ ok }) => setSaveFailing(!ok)), []);
  const [resetKey, setResetKey]   = useState(0);
  const [header, setHeader]       = useState({ jobno:"", date:today, tech:"", make:"", model:"", serial:"", tankerModel:"", tankerSerial:"", rego:"", pto:"", nextsvc:"", customer:"", contact:"", modelVariant:"" });
  const [sheets, setSheets]       = useState(() => cleanupCompleted());
  const [missing, setMissing]     = useState([]);   // required items still to do (shown after a download attempt)
  const currentJobno = useRef("");

  // Auto-save on every change — rename slot if jobno changed
  useEffect(() => {
    if (screen !== "sheet" || !configKey) return;
    const key = header.sheetId || header.jobno || "__draft__";
    if (currentJobno.current && currentJobno.current !== key) {
      deleteSheet(currentJobno.current);
    }
    currentJobno.current = key;
    saveSheet(key, { configKey, state, collapsed, header });
  }, [configKey, state, collapsed, header, screen]);

  const openSheet = (sheet) => {
    setConfigKey(sheet.configKey);
    setState(migrateSheetState(sheet));
    setCollapsed(sheet.collapsed || {});
    setHeader(sheet.header);
    currentJobno.current = sheet.jobno;
    setScreen("sheet");
  };

  const newSheet = (key, modelId="", equipDisplay="", equipTypeId="") => {
    setConfigKey(key);
    setState(initState(CONFIGS[key], getServiceActionsForModel(key, modelId), getSectionsForModel(key, modelId)));
    setCollapsed({});
    setSaved("");
    setHeader({ jobno:"", date:today, tech:"", ...modelHeader(modelId), serial:"", tankerModel:"", tankerSerial:"", rego:"", pto:"", nextsvc:"", customer:"", contact:"", modelVariant:modelId, equipDisplay, equipType:equipTypeId,
      sheetId:`s${Date.now().toString(36)}${Math.random().toString(36).slice(2,6)}`, startedAt:new Date().toISOString() });
    setResetKey(k => k + 1);
    currentJobno.current = "";
    setScreen("sheet");
  };

  const goHome = () => { setSheets(cleanupCompleted()); setScreen("home"); setMissing([]); };

  // Resolves true once the sheet is safely written to the device
  const manualSave = async () => {
    if (document.activeElement && typeof document.activeElement.blur === "function") document.activeElement.blur();
    const key = header.sheetId || header.jobno || "__draft__";
    currentJobno.current = key;
    const ok = await saveSheet(key, { configKey, state, collapsed, header });
    if (ok) { setJustSaved(true); setTimeout(() => setJustSaved(false), 1800); }
    else window.alert("This sheet could NOT be saved on this device. Don't close the app — use \"Save photos to phone\" and download the PDF, then free up space on the phone.");
    return ok;
  };

  const config = configKey ? CONFIGS[configKey] : null;
  // Model options for the in-sheet "change model" dropdown come from the top-level
  // equipment type the tech originally chose (header.equipType) — not from the
  // config's internal template group, since e.g. both AV850 (vacuum template) and
  // AV2000H (hydrovac template) are picked from the same "Vacuum" equipment type.
  // Older saved sheets predate equipType being stored, so fall back to the template
  // group (translating the old "hydrovac" template back to the "vacuum" tile).
  const rawGroup = configKey ? getModelGroup(configKey) : null;
  const equipType = header.equipType || (rawGroup === "hydrovac" ? "vacuum" : rawGroup);
  // Only offer models whose template matches the sheet's current template (plus
  // "Generic") — switching to a model built for the OTHER template here would
  // change the service actions shown without changing the inspection questions,
  // which would silently mismatch the two. A full template switch requires
  // starting a new checksheet from the model picker instead.
  const modelOptions = (equipType && MODEL_OPTIONS[equipType])
    ? MODEL_OPTIONS[equipType].filter(m => (m.id==="" || m.template===rawGroup) && (!m.hidden || m.id===header.modelVariant))
    : null;
  const effectiveServiceActions = config ? getServiceActionsForModel(configKey, header.modelVariant) : [];
  const effectiveSections = config ? getSectionsForModel(configKey, header.modelVariant) : [];
  const displayEquipment = header.equipDisplay || config?.equipment;
  const effectiveConfig = config ? {
    ...config,
    serviceActions: effectiveServiceActions,
    sections: effectiveSections,
    hirePhotos: getHirePhotosForModel(configKey, header.modelVariant),
    equipment: displayEquipment,
    label: `${displayEquipment} — ${serviceLabel(config)}`,
  } : null;
  const selectConfig = (equipTypeId, modelId, service) => {
    const template = resolveTemplate(equipTypeId, modelId);
    const key = `${template}-${service}`;
    const equipLabel = EQUIPMENT_TYPES.find(e=>e.id===equipTypeId)?.label || template;
    newSheet(key, modelId, equipLabel, equipTypeId);
  };
  const toggleSection = id => setCollapsed(c=>({...c,[id]:!c[id]}));

  // Changing the model swaps which service-action checklist applies, and may add
  // model-specific inspection sections (e.g. AV2000/AV850's engine checks). Only
  // crossover-compatible data survives the switch: the shared template sections
  // (cab, chassis, functional), certs, photos, notes, defects and signature are
  // untouched — but any model-specific section that doesn't apply to the newly
  // selected model gets cleared out rather than left behind with stale answers.
  const changeModel = (modelId) => {
    setHeader(h => ({ ...h, modelVariant: modelId, ...modelHeader(modelId) }));
    const newActions = getServiceActionsForModel(configKey, modelId);
    const newSections = getSectionsForModel(configKey, modelId);
    const newExtraIds = new Set(newSections.map(s=>s.id).filter(id => ALL_MODEL_SECTION_EXTRA_IDS.includes(id)));
    setState(p => {
      const sa = {};
      newActions.forEach(item => {
        sa[item.key] = null;
        if (item.litres)    sa[item.litres]    = "";
        if (item.condition) sa[item.condition] = "";
      });
      const next = { ...p, serviceActions: sa };
      // Drop stale model-specific sections that no longer apply
      ALL_MODEL_SECTION_EXTRA_IDS.forEach(id => { if (!newExtraIds.has(id)) delete next[id]; });
      // Add fresh state for newly-applicable model-specific sections
      newSections.forEach(sec => {
        if (!ALL_MODEL_SECTION_EXTRA_IDS.includes(sec.id) || next[sec.id]) return;
        const fresh = {};
        sec.items.forEach((_,i) => { fresh[i] = {status:null,note:"",photos:[]}; });
        next[sec.id] = fresh;
      });
      return next;
    });
  };

  const updateInspection    = (sid,idx,f,v) => setState(p=>({...p,[sid]:{...p[sid],[idx]:{...p[sid][idx],[f]:v}}}));
  const updateCert          = (k,f,v)       => setState(p=>({...p,certs:{...p.certs,[k]:{...p.certs[k],[f]:v}}}));
  const updateServiceAction = (k,v)         => setState(p=>({...p,serviceActions:{...p.serviceActions,[k]:v}}));
  const updateOperational   = (k,v)         => setState(p=>({...p,operationalChecks:{...p.operationalChecks,[k]:v}}));
  const updateOilPhoto      = (v)           => setState(p=>({...p,oilSamplePhoto:v}));
  const updateVehiclePhoto  = (k,v)         => setState(p=>({...p,vehiclePhotos:{...p.vehiclePhotos,[k]:v}}));
  const updateHirePhoto     = (k,fn)        => setState(p=>({...p,hirePhotos:{...(p.hirePhotos||{}),[k]:fn(hirePhotoList(p,k))}}));
  const updateOtherNotes = (idx, field, value) => setState(p => {
    if (idx === "addNew") {
      const last = p.otherNotes[p.otherNotes.length-1];
      if (last.text || last.photos?.length>0) {
        return { ...p, otherNotes:[...p.otherNotes, {text:"",photos:[],sendToZendesk:true}] };
      }
      return p;
    }
    const updated = p.otherNotes.map((n,i) => {
      if(i!==idx) return n;
      if(field==="photo") return {...n, photos:[...n.photos, value]};
      return {...n, [field]:value};
    });
    return {...p, otherNotes:updated};
  });
  const updateDefectRect = (failKey, field, value) => setState(p=>({
    ...p,
    defects: p.defects.map(d => d.failKey===failKey ? {...d,[field]:value} : d)
  }));

  // When item fails/unfails — manage defects array
  // `extra` (optional) sets a starting recommendation, e.g. a pre-rebuild cert
  // renewal to offer; it never overwrites one the tech has written.
  const handleFail = (secId, itemIdx, itemLabel, isFailing, note, photos, extra) => {
    setState(p => {
      const failKey = `${secId}-${itemIdx}`;
      const defectText = note ? `${itemLabel} — ${note}` : itemLabel;
      if (isFailing) {
        const existing = p.defects.findIndex(d => d.failKey===failKey);
        if (existing !== -1) {
          // Update existing entry (note/photo sync)
          const updated = [...p.defects];
          const cur = updated[existing];
          const { recommendation, ...rest } = extra || {};
          updated[existing] = { ...cur, ...rest, defect: defectText, photos: photos||cur.photos,
            ...(recommendation && !cur.recommendation ? { recommendation } : {}) };
          return { ...p, defects: updated };
        } else {
          // Add new defect entry
          return { ...p, defects: [...p.defects, { failKey, defect: defectText, photos: photos||[], rect:"", ...(extra || {}) }] };
        }
      } else {
        // Remove the defect entry, plus any extra faults logged on the same item
        const defects = p.defects.filter(d => d.failKey!==failKey && !(d.failKey||"").startsWith(`${failKey}#`));
        if (secId === "certs" || !p[secId]?.[itemIdx]?.extraFaults?.length) return { ...p, defects };
        return { ...p, defects, [secId]: { ...p[secId], [itemIdx]: { ...p[secId][itemIdx], extraFaults: [] } } };
      }
    });
    if (isFailing) setCollapsed(c=>({...c, defects:false}));
  };

  // Extra faults on one failed item: each is its own defect line, kept right
  // after that item's other defects. `faults` = [{ id, note, photos }].
  const setExtraFaults = (secId, itemIdx, itemLabel, faults) => setState(p => {
    const base = `${secId}-${itemIdx}`, keyOf = f => `${base}#${f.id}`;
    const ours = d => d.failKey === base || (d.failKey || "").startsWith(`${base}#`);
    let defects = p.defects.filter(d => !(d.failKey || "").startsWith(`${base}#`) || faults.some(f => keyOf(f) === d.failKey));
    faults.forEach(f => {
      const text = f.note ? `${itemLabel} — ${f.note}` : itemLabel;
      const at = defects.findIndex(d => d.failKey === keyOf(f));
      if (at !== -1) { defects[at] = { ...defects[at], defect: text, photos: f.photos || [] }; return; }
      let last = -1; defects.forEach((d, j) => { if (ours(d)) last = j; });
      const entry = { failKey: keyOf(f), defect: text, photos: f.photos || [], rect: "" };
      defects = last === -1 ? [...defects, entry] : [...defects.slice(0, last + 1), entry, ...defects.slice(last + 1)];
    });
    return { ...p, defects, [secId]: { ...p[secId], [itemIdx]: { ...p[secId][itemIdx], extraFaults: faults } } };
  });

  // Photo moves / copies and multi-select-safe adds (see photoListAt / setPhotoListAt)
  const updatePhotos = (loc, fn) => setState(p => setPhotoListAt(p, loc, fn(photoListAt(p, loc))));
  const movePhoto = (to, copy) => {
    const { loc: from, src } = moving;
    setState(p => {
      let q = copy ? p : setPhotoListAt(p, from, photoListAt(p, from).filter(x => x !== src));
      const dest = photoListAt(q, to);
      return dest.includes(src) ? q : setPhotoListAt(q, to, [...dest, src]);
    });
    setMoving(null);
  };
  const photoCtx = { update: updatePhotos, openMove: (loc, src) => setMoving({ loc, src }) };

  // Post-rebuild: pre-rebuild results imported from its PDF or a sheet on this device
  const preRebuild = config?.comparesWith ? state.preRebuild || null : null;
  const importRebuild = (rebuild, src = {}) => {
    setState(p => ({ ...p, preRebuild: { ...rebuild, importedAt: new Date().toISOString() } }));
    // Fill unit details the tech hasn't typed yet from the pre-rebuild sheet
    setHeader(h => { const fill = {}; ["make","model","serial","rego","customer","contact"].forEach(k => { if (!h[k] && src[k]) fill[k] = src[k]; }); return { ...h, ...fill }; });
  };
  const importRebuildPdf = async file => { const { rebuild, header: h } = await readRebuildFromPdf(file); importRebuild(rebuild, h); };
  const importRebuildSheet = sh => {
    const cfg = CONFIGS[sh.configKey];
    importRebuild(rebuildResults({ ...cfg, sections: getSectionsForModel(sh.configKey, sh.header?.modelVariant) }, sh.header || {}, sh.state || {}), sh.header || {});
  };

  // ── Home screen ──────────────────────────────────────────────────────────────
  if (screen === "home") return (
    <div style={{ minHeight:"100vh", background:v("bg") }}>
      <div style={{ background:"#1E5FA8", padding:"0 1.5rem", height:56, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <img src={LOGO_B64} alt="Waimea Group" style={{ height:34 }} />
        <button onClick={toggleDark} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:20, padding:"5px 10px", cursor:"pointer", fontSize:16, color:"#fff" }}>{dark?"☀️":"🌙"}</button>
      </div>
      <div style={{ maxWidth:540, margin:"0 auto", padding:"1.5rem 1rem" }}>

        {/* Auto-save camera photos to the device (Android phones / tablets) */}
        {CAN_AUTOSAVE && (
          // On/off switch (a plain checkbox shows no tick here — the global input style strips it)
          <button type="button" role="switch" aria-checked={autoSave} onClick={()=>{ setAutoSavePhotos(!autoSave); setAutoSave(!autoSave); }}
            style={{ width:"100%", display:"flex", alignItems:"center", gap:12, textAlign:"left", background:v("surface"), border:`1px solid ${v("border")}`, borderRadius:12, padding:"12px 16px", marginBottom:"1.5rem", fontSize:14, color:v("text"), cursor:"pointer", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
            <span style={{ flex:1 }}><b>Auto-save photos to this device</b><br /><span style={{ color:v("text-sec"), fontSize:13 }}>Each camera photo is also saved to Downloads / Gallery</span></span>
            <span style={{ fontSize:12, fontWeight:700, color:autoSave?"#059669":v("text-ter"), minWidth:24 }}>{autoSave?"ON":"OFF"}</span>
            <span aria-hidden="true" style={{ position:"relative", width:48, height:28, flexShrink:0, borderRadius:14, background:autoSave?"#10B981":v("divider"), transition:"background 150ms" }}>
              <span style={{ position:"absolute", top:3, left:autoSave?23:3, width:22, height:22, borderRadius:"50%", background:"#fff", boxShadow:"0 1px 3px rgba(0,0,0,0.3)", transition:"left 150ms" }} />
            </span>
          </button>
        )}

        {/* New sheet */}
        <div style={{ fontSize:13, fontWeight:700, color:v("text-sec"), textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>New Sheet</div>
        <SelectorScreen onSelect={selectConfig}>
        {/* Saved sheets */}
        {sheets.length > 0 && (
          <div style={{ marginTop:"1.5rem" }}>
            <div style={{ fontSize:13, fontWeight:700, color:v("text-sec"), textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>Saved Sheets</div>
            {sheets.map(sheet => (
              <div key={sheet.jobno} style={{ background:v("surface"), border:`1px solid ${v("border")}`, borderRadius:12, padding:"14px 16px", marginBottom:10, display:"flex", alignItems:"center", gap:12, boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:15, color:v("text") }}>
                    {sheet.header?.jobno ? `Job ${sheet.header.jobno}` : "Draft (no job no.)"}
                    {sheet.header?.model || sheet.header?.tankerModel ? ` · ${sheet.header?.model || sheet.header?.tankerModel}` : ""}
                  </div>
                  <div style={{ fontSize:12, color:v("text-sec"), marginTop:2 }}>
                    {sheet.state?.completedAt ? <b style={{ color:"#10B981" }}>✓ Completed · </b> : null}{sheet.header?.rego ? `${sheet.header.rego} · ` : ""}{sheet.header?.customer ? `${sheet.header.customer} · ` : ""}{sheet.header?.equipDisplay ? `${sheet.header.equipDisplay} — ${serviceLabel(CONFIGS[sheet.configKey])}` : (CONFIGS[sheet.configKey]?.label || sheet.configKey)} · {new Date(sheet.savedAt).toLocaleString("en-NZ",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                  </div>
                </div>
                <button onClick={()=>openSheet(sheet)}
                  style={{ padding:"8px 16px", borderRadius:8, border:"none", background:"#1E5FA8", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", flexShrink:0 }}>
                  {sheet.state?.completedAt ? "View" : "▶ Open"}
                </button>
                <button onClick={()=>{ if(window.confirm(`Delete ${sheet.header?.jobno?`job ${sheet.header.jobno}`:"this draft"}?`)){ deleteSheet(sheet.jobno); setSheets(getAllSheets()); } }}
                  style={{ padding:"8px 10px", borderRadius:8, border:`1px solid ${v("border")}`, background:"transparent", color:v("text-sec"), fontSize:13, cursor:"pointer", flexShrink:0 }}>
                  🗑
                </button>
              </div>
            ))}
          </div>
        )}

        </SelectorScreen>
      </div>
    </div>
  );

  const hf = headerFieldsFor(config, header);
  const allMissing = findMissing(config, effectiveConfig, header, state);
  const required = collectRequired(config, effectiveConfig, header, state);
  const total = required.length, done = total - allMissing.length;
  const pct = total ? Math.floor(done/total*100) : 0;

  // Jump to and flash the first incomplete required item.
  const goToMissing = (m) => {
    if (m.sec) setCollapsed(c => ({ ...c, [m.sec]: false }));
    setTimeout(() => {
      const el = document.querySelector(`[data-req="${m.req}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior:"smooth", block:"center" });
      el.classList.remove("req-flash"); void el.offsetWidth; el.classList.add("req-flash");
      clearTimeout(el._flashT); el._flashT = setTimeout(() => el.classList.remove("req-flash"), 2400); // flash, then clear
      const f = el.querySelector("input, button"); if (f && m.req.startsWith("h:")) setTimeout(()=>f.focus({ preventScroll:true }), 400);
    }, 80);
  };
  const tryDownload = () => {
    if (document.activeElement && typeof document.activeElement.blur === "function") document.activeElement.blur();
    const miss = findMissing(config, effectiveConfig, header, state);
    if (miss.length) { setMissing(miss); goToMissing(miss[0]); return; }
    setMissing([]);
    const completedAt = state.completedAt || new Date().toISOString();
    const finalState = { ...state, completedAt };
    const f = exportPDF(effectiveConfig, header, finalState, LOGO_B64);
    setState(finalState);
    setSaved(f);
  };

  // ── Completed sheet: locked, view / re-download only ─────────────────────────
  if (state.completedAt) return (
    <div style={{ minHeight:"100vh", background:v("bg") }}>
      <div style={{ background:"#1E5FA8", padding:"0 1.5rem", height:56, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <img src={LOGO_B64} alt="Waimea Group" style={{ height:34 }} />
        <span style={{ fontSize:12, padding:"3px 12px", borderRadius:100, background:serviceColor(config), color:"#fff", fontWeight:700 }}>{serviceLabel(config)}</span>
      </div>
      <div style={{ maxWidth:640, margin:"0 auto", padding:"1.5rem 1rem 3rem" }}>
        <div style={{ background:v("surface"), border:`1px solid ${v("border")}`, borderRadius:12, padding:"1.5rem", boxShadow:"0 1px 4px rgba(0,0,0,0.07)" }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#10B981", textTransform:"uppercase", letterSpacing:"0.06em" }}>✓ Completed — locked</div>
          <div style={{ fontSize:20, fontWeight:700, color:v("text"), margin:"6px 0 4px" }}>{effectiveConfig.label}</div>
          <div style={{ fontSize:14, color:v("text-sec"), lineHeight:1.7 }}>
            Job <b>{header.jobno}</b> · Rego <b>{header.rego||"—"}</b>{header.model?<> · {[header.make,header.model].filter(Boolean).join(" ")}</>:null}<br/>
            Technician {header.tech||"—"} · completed {new Date(state.completedAt).toLocaleString("en-NZ",{dateStyle:"medium",timeStyle:"short"})}<br/>
            {(state.defects||[]).filter(d=>d.defect).length} defect(s) recorded
          </div>
          <p style={{ fontSize:13, color:v("text-sec"), background:v("surface2"), borderRadius:8, padding:"10px 12px", margin:"14px 0 0" }}>
            Completed checksheets can't be edited or reused. For another unit or another visit, start a new checksheet. This sheet is removed from this device after {KEEP_COMPLETED_DAYS} days — the PDF in WFM job docs is the record.
          </p>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginTop:16 }}>
            <Btn variant="primary" onClick={()=>{ const f=exportPDF(effectiveConfig,header,state,LOGO_B64); setSaved(f); }}>⬇ Download PDF again</Btn>
            <Btn variant="success" onClick={goHome}>+ Start a new checksheet</Btn>
            <Btn variant="ghost" onClick={goHome}>⌂ Home</Btn>
          </div>
          {saved && <div style={{ marginTop:14, fontSize:14, color:"var(--success-text)" }}>✓ Saved as <strong>{saved}</strong></div>}
        </div>
      </div>
    </div>
  );

  const hRow = fields => fields.length ? (
    <div style={{ display:"grid", gridTemplateColumns:fields.map(()=>"minmax(0, 1fr)").join(" "), gap:12, marginBottom:12 }}>
      {fields.map(id=>(
        <div key={id} data-req={`h:${id}`} style={{ display:"flex", flexDirection:"column", gap:5, borderRadius:8 }}>
          <Label>{(FIELD_LABELS[id]||id)}{OPTIONAL_FIELDS.has(id)?"":" *"}</Label>
          {id==="jobno" ? (
            // Job number always starts J00 — techs type only the digits after it.
            <div style={{ display:"flex", alignItems:"stretch" }}>
              <span style={{ display:"flex", alignItems:"center", padding:"0 6px", fontSize:14, fontWeight:700, background:v("surface2"), border:`1px solid ${v("border")}`, borderRight:"none", borderRadius:"8px 0 0 8px", color:v("text") }}>J00</span>
              <FieldInput inputMode="numeric" placeholder="1234" style={{ borderRadius:"0 8px 8px 0", padding:"12px 6px" }}
                value={(header.jobno||"").replace(/^J00/i,"")}
                onChange={e=>{ const d=e.target.value.replace(/^\s*J0{0,2}/i,"").replace(/\D/g,""); setHeader(h=>({...h, jobno: d ? `J00${d}` : ""})); }} />
            </div>
          ) : id==="date" ? (
            <DateWheelField value={header.date||""} onChange={val=>setHeader(h=>({...h, date:val}))} style={{ fontSize:14, padding:"12px 8px" }} />
          ) : (
            <FieldInput value={header[id]||""} onChange={e=>setHeader(h=>({...h,[id]:e.target.value}))} />
          )}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <PhotoCtx.Provider value={photoCtx}>
    <div style={{ minHeight:"100vh", background:v("bg") }}>
      <div style={{ background:"#1E5FA8", padding:"0 1.5rem", height:56, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:10 }}>
        <img src={LOGO_B64} alt="Waimea Group" style={{ height:34 }} />
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:13, color:"rgba(255,255,255,0.85)" }}>{effectiveConfig.label}</span>
          <span style={{ fontSize:12, padding:"3px 12px", borderRadius:100, background:serviceColor(config), color:"#fff", fontWeight:700 }}>{serviceLabel(config)}</span>
          <button onClick={toggleDark} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:20, padding:"5px 10px", cursor:"pointer", fontSize:16, color:"#fff" }}>{dark?"☀️":"🌙"}</button>
        </div>
      </div>

      <div key={resetKey} style={{ maxWidth:860, margin:"0 auto", padding:"1.25rem 1rem 3rem" }}>
        <button onClick={goHome} style={{ fontSize:13, color:v("text-sec"), background:"transparent", border:"none", cursor:"pointer", padding:"0 0 1rem", display:"flex", alignItems:"center", gap:4 }}>← All checksheets</button>

        {config.draft && (
          <div style={{ background:"#FEF3C7", border:"1px solid #F59E0B", borderRadius:10, padding:"12px 16px", marginBottom:"1rem", fontSize:13, color:"#92400E", fontWeight:600 }}>
            ⚠ Draft / placeholder checksheet — inspection items and service actions are provisional and pending review. Do not treat as final.
          </div>
        )}

        {/* Header card */}
        <div style={{ background:v("surface"), border:`0.5px solid ${v("border")}`, borderRadius:12, overflow:"hidden", marginBottom:"1.25rem", boxShadow:"0 1px 4px rgba(0,0,0,0.07)" }}>
          <div style={{ background:`linear-gradient(135deg, ${"#1E5FA8"} 0%, #2471C8 100%)`, padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div>
              <div style={{ fontSize:18, fontWeight:700, color:"#fff" }}>{effectiveConfig.equipment} — {serviceLabel(config)} Checksheet</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", marginTop:3 }}>Waimea Group · Specialist Utility Vehicles</div>
            </div>
            <span style={{ fontSize:13, padding:"4px 14px", borderRadius:100, background:serviceColor(config), color:"#fff", fontWeight:700 }}>{serviceLabel(config)}</span>
          </div>
          <div style={{ padding:"1.5rem" }}>
            <div style={{ fontSize:12, fontWeight:700, color:"var(--info-text)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>Job details</div>
            {hRow(["jobno","date","tech"])}
            {hRow(hf.job.filter(f=>f==="customer"||f==="contact"))}{/* off-hire / pre-hire: Waimea's own rental fleet — no customer/contact */}
            {hf.eq.some(f=>["make","model","serial"].includes(f)) && <>
              <div style={{ fontSize:12, fontWeight:700, color:"var(--info-text)", textTransform:"uppercase", letterSpacing:"0.06em", margin:"14px 0 10px" }}>{effectiveConfig.equipment}{header.model?` — ${header.make?header.make+" ":""}${header.model}`:header.make?` — ${header.make}`:""}</div>
              {hRow(hf.eq.filter(f=>["make","model","serial"].includes(f)))}
            </>}
            {!hf.eq.some(f=>["make","model","serial"].includes(f)) && (header.model||header.make) && (
              <div style={{ fontSize:13, color:v("text-sec"), margin:"4px 0 12px" }}>{effectiveConfig.equipment}: <b style={{ color:v("text") }}>{[header.make, header.model].filter(Boolean).join(" ")}</b></div>
            )}
            {hf.eq.some(f=>["tankerModel","tankerSerial"].includes(f)) && <>
              <div style={{ fontSize:12, fontWeight:700, color:"var(--info-text)", textTransform:"uppercase", letterSpacing:"0.06em", margin:"14px 0 10px" }}>Tanker</div>
              {hRow(hf.eq.filter(f=>["tankerModel","tankerSerial"].includes(f)))}
            </>}
            {modelOptions && (
              <div style={{ display:"flex", flexDirection:"column", gap:5, marginBottom:12 }}>
                <Label>Equipment model (sets Service Actions checklist)</Label>
                <select
                  value={header.modelVariant||""}
                  onChange={e=>changeModel(e.target.value)}
                  style={{ fontSize:14, background:v("input"), color:v("text"), border:`1px solid ${v("border")}`, borderRadius:8, padding:"12px", width:"100%", outline:"none" }}
                >
                  {modelOptions.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
            )}
            <div style={{ fontSize:12, fontWeight:700, color:"var(--info-text)", textTransform:"uppercase", letterSpacing:"0.06em", margin:"14px 0 10px" }}>Vehicle</div>
            {hRow(hf.vf.slice(0,3))}{hf.vf.length>3 && hRow(hf.vf.slice(3))}
          </div>
        </div>

        {/* Progress — sticky so it stays visible while scrolling through the sheet */}
        <div style={{ position:"sticky", top:0, zIndex:20, background:v("bg"), paddingTop:"0.75rem", paddingBottom:"0.75rem", marginBottom:"0.5rem", boxShadow:"0 4px 8px -4px rgba(0,0,0,0.15)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, color:v("text-sec"), marginBottom:8 }}>
            <span>{done} of {total} required items done{allMissing.length ? ` · ${allMissing.length} left` : " · ready to download"}</span>
            <span style={{ fontWeight:600, color:pct===100?"#10B981":v("text-sec") }}>{pct}%</span>
          </div>
          <div style={{ background:"#dce3ec", borderRadius:100, height:8, overflow:"hidden" }}>
            <div style={{ height:8, borderRadius:100, background:pct===100?"#10B981":"#2BACD1", width:pct+"%", transition:"width 0.3s" }} />
          </div>
        </div>

        {/* Post-rebuild: import + compare with the pre-rebuild */}
        {config.comparesWith && <RebuildImportPanel pre={preRebuild} measurements={config.measurements || []} measures={state.measures}
          deviceSheets={getAllSheets().filter(s => s.configKey === "ewp-pr")}
          onImportPdf={importRebuildPdf} onImportSheet={importRebuildSheet}
          onClear={() => setState(p => ({ ...p, preRebuild: null }))}
          collapsed={!!collapsed["rebuild"]} onToggle={() => toggleSection("rebuild")} />}

        {/* Certs */}
        {config.certs?.length>0 && <CertsSection config={config} certs={config.certs} secState={state.certs} before={preRebuild?.certs} onUpdate={(k,f,v)=>updateCert(k,f,v)} onFail={handleFail} collapsed={!!collapsed["certs"]} onToggle={()=>toggleSection("certs")} />}

        {/* Vehicle photos — after certs, before inspections */}
        <VehiclePhotosSection
          photos={state.vehiclePhotos}
          onUpdate={updateVehiclePhoto}
          collapsed={!!collapsed["vehiclePhotos"]}
          onToggle={()=>toggleSection("vehiclePhotos")} />

        {/* Condition photos — vacuum-family off-hire / pre-hire only */}
        {effectiveConfig.hirePhotos?.length>0 && <HirePhotosSection
          slots={effectiveConfig.hirePhotos}
          state={state}
          onUpdate={updateHirePhoto}
          collapsed={!!collapsed["hirePhotos"]}
          onToggle={()=>toggleSection("hirePhotos")} />}

        {/* Sections — with vehicle photos injected after cab */}
        {(() => {
          let globalFailCount = 0;
          return effectiveConfig.sections.map(sec => {
            const failsBefore = globalFailCount;
            sec.items.forEach((_,i) => { if(state[sec.id]?.[i]?.status==="fail") globalFailCount++; });
            return (
              <div key={sec.id}>
                <InspectionSection sec={sec} secState={state[sec.id]}
                  onUpdate={(i,f,v)=>updateInspection(sec.id,i,f,v)}
                  onFail={handleFail}
                  onFaults={setExtraFaults}
                  before={preRebuild?.sections?.[sec.id]}
                  itemPhotos
                  failStartNum={failsBefore}
                  sectionNote={state.sectionNotes?.[sec.id]} onSectionNote={val=>setState(p=>({...p, sectionNotes:{...(p.sectionNotes||{}), [sec.id]:val}}))}
                  collapsed={!!collapsed[sec.id]} onToggle={()=>toggleSection(sec.id)} />
                {/* Vehicle photos removed from here — now sits after service actions */}
              </div>
            );
          });
        })()}

        {/* Operational checks */}
        {config.operationalChecks && <OperationalChecksSection secState={state.operationalChecks} onUpdate={updateOperational} collapsed={!!collapsed["operational"]} onToggle={()=>toggleSection("operational")} />}

        {/* Service actions */}
        {effectiveConfig.serviceActions?.length>0 && <ServiceActionsSection config={effectiveConfig} saState={state.serviceActions} onUpdate={updateServiceAction} onPhotoUpdate={updateOilPhoto} oilSamplePhoto={state.oilSamplePhoto} collapsed={!!collapsed["service"]} onToggle={()=>toggleSection("service")} />}

        {/* Readings — pre-rebuild cycle times, pump test, deflection */}
        {(config.measurements||[]).map(g => (
          <MeasurementsSection key={g.id} group={g} values={state.measures} photos={state.measurePhotos?.[g.id]}
            onValue={(k,val)=>setState(p=>({...p, measures:{...(p.measures||{}), [k]:val}}))}
            onPhotos={ph=>setState(p=>({...p, measurePhotos:{...(p.measurePhotos||{}), [g.id]:ph}}))}
            before={preRebuild?.measures} requireTargets={config.service === "PR"}
            collapsed={!!collapsed[`m-${g.id}`]} onToggle={()=>toggleSection(`m-${g.id}`)} />
        ))}

        {/* Defects — auto-populated from fails only */}
        <DefectsSection hideRect={["OH","PH","PR","PO"].includes(config.service)} defects={state.defects} onUpdate={updateDefectRect} collapsed={!!collapsed["defects"]} onToggle={()=>toggleSection("defects")} />

        {/* Other notes */}
        <OtherNotesSection hideRect={["OH","PH","PR","PO"].includes(config.service)} notes={state.otherNotes} onUpdate={updateOtherNotes} defectCount={state.defects.filter(d=>d.defect).length} collapsed={!!collapsed["otherNotes"]} onToggle={()=>toggleSection("otherNotes")} />

        {/* Signature */}
        <div data-req="sig" style={{ border:`0.5px solid ${v("border")}`, borderRadius:10, marginBottom:"1rem", overflow:"hidden", boxShadow:"0 1px 3px rgba(0,0,0,0.06)", background:v("surface"), padding:"16px" }}>
          <SignaturePad C={C} value={state.signature} onChange={sig=>setState(p=>({...p, signature:sig}))} />
        </div>
        {config.teamLeaderSignoff && (
          <div data-req="tlsig" style={{ border:`0.5px solid ${v("border")}`, borderRadius:10, marginBottom:"1rem", overflow:"hidden", boxShadow:"0 1px 3px rgba(0,0,0,0.06)", background:v("surface"), padding:"16px" }}>
            <SignaturePad C={C} label="Team Leader Sign Off" value={state.tlSignature} onChange={sig=>setState(p=>({...p, tlSignature:sig}))} />
            <FieldInput style={{ fontSize:13, padding:"8px 10px", marginTop:10 }} placeholder="Team leader name" defaultValue={header.tlName||""} onBlur={e=>setHeader(h=>({...h, tlName:e.target.value}))} />
          </div>
        )}

        {/* Actions */}
        <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginTop:24, paddingTop:24, borderTop:`1px solid ${v("border")}` }}>
          <Btn variant="primary" onClick={tryDownload} style={allMissing.length?{ opacity:0.75 }:undefined}>⬇ Download PDF{allMissing.length?` (${allMissing.length} left)`:""}</Btn>
          <Btn variant="success" onClick={()=>{
            const defectCount = (state.defects||[]).filter(d=>d.defect).length;
            const defectLines = (state.defects||[]).filter(d=>d.defect && d.sendToZendesk!==false)
              .map((d,i)=>`#${i+1}: ${d.defect}${d.rect?` — ${d.rect}`:""}`)
              .join("\n");
            const noteLines = (state.otherNotes||[]).filter(n=>n.text && n.sendToZendesk!==false)
              .map((n,i)=>`#${defectCount+i+1}: ${n.text}`)
              .join("\n");
            const description = [defectLines, noteLines].filter(Boolean).join("\n") || "No items selected";
            const params = new URLSearchParams({
              "Form Date Field": header.date||today,
              "Your Name": header.tech||"",
              "Job Number": header.jobno||"",
              "Rego": header.rego||"",
              "Equipment Model": header.model||header.tankerModel||"",
              "Company": header.customer||"",
              "Contact": header.contact||"",
              "Request Type": "Defect",
              "Description": description,
              "Send to Zendesk?": "true",
            });
            window.open(`https://app.smartsheet.com/b/form/6371f395ba0b4ad696f698260ab908cb?${params.toString()}`, "_blank");
          }}>↗ Send to Zendesk</Btn>
          <Btn variant="secondary" onClick={()=>{ if(window.confirm("Reset this checksheet? All data will be cleared.")) { deleteSheet(header.sheetId||header.jobno||"__draft__"); setState(initState(config, effectiveServiceActions, effectiveSections)); setSaved(""); setHeader(h=>({ jobno:"", date:today, tech:"", make:"", model:"", serial:"", tankerModel:"", tankerSerial:"", rego:"", pto:"", nextsvc:"", customer:"", contact:"", modelVariant:h.modelVariant, equipDisplay:h.equipDisplay })); setResetKey(k=>k+1); } }}>↺ Reset</Btn>
          <Btn variant="success" onClick={async ()=>{ if (await manualSave()) goHome(); }}>💾 Save & Close</Btn>
          <Btn variant="secondary" onClick={()=>savePhotosToDevice(state, header)}>📷 Save photos to phone</Btn>
          <Btn variant="ghost" onClick={goHome}>⌂ Home</Btn>
          <Btn variant="ghost" onClick={goHome}>← Change type</Btn>
        </div>

        {missing.length > 0 && allMissing.length > 0 && (
          <div style={{ marginTop:16, padding:"14px 18px", background:"var(--danger-bg)", border:"1px solid #EF4444", borderRadius:10, fontSize:14, color:"var(--danger-text)" }}>
            <b>Can't download yet — {allMissing.length} required item{allMissing.length>1?"s":""} still to complete.</b>
            <ul style={{ margin:"8px 0 0", paddingLeft:18, lineHeight:1.7 }}>
              {allMissing.slice(0,8).map(m=>(
                <li key={m.req}><button onClick={()=>goToMissing(m)} style={{ background:"none", border:"none", padding:0, color:"inherit", textDecoration:"underline", cursor:"pointer", fontSize:14, textAlign:"left" }}>{m.label}</button></li>
              ))}
              {allMissing.length>8 && <li>…and {allMissing.length-8} more</li>}
            </ul>
          </div>
        )}
        {saveFailing && (
          <div role="alert" style={{ position:"fixed", top:0, left:0, right:0, zIndex:1001, background:"#B42318", color:"#fff", padding:"10px 16px", fontSize:14, fontWeight:600, textAlign:"center", lineHeight:1.4 }}>
            ⚠ This sheet is NOT saving on this device. Don't close the app — tap "Save photos to phone" and download the PDF, then free up space.
          </div>
        )}
        {justSaved && (
          <div style={{ marginTop:16, padding:"12px 18px", background:"var(--success-bg)", border:"1px solid var(--success-border)", borderRadius:10, fontSize:14, color:"var(--success-text)" }}>
            ✓ Saved
          </div>
        )}

        {saved && (
          <div style={{ marginTop:16, padding:"14px 18px", background:"var(--success-bg)", border:"1px solid var(--success-border)", borderRadius:10, fontSize:14, color:"var(--success-text)", lineHeight:1.6 }}>
            ✓ Saved as <strong>{saved}</strong><br />
            <span style={{ color:v("text-sec") }}>Go to WorkflowMax → Job {header.jobno||"…"} → Documents → Upload New Document</span>
          </div>
        )}
      </div>
    </div>
    <MovePhotoSheet moving={moving} targets={moving ? photoTargets(state, effectiveConfig) : []} onMove={movePhoto} onClose={()=>setMoving(null)} />
    </PhotoCtx.Provider>
  );
}
